import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { CloudDownloadOutlined } from '@ant-design/icons';
import { Alert, Collapse, Input, message, Modal, notification, Pagination, Spin } from 'antd';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button, PageHeader, SearchableSelect } from '@/shared/components';
import type { SearchableOption } from '@/shared/components';
import { budgetsApi } from '@/features/budgets/api';
import {
  CALC_BAND_NEW_ID,
  mapParentTuitionBandsToEditableRows,
  mapSchoolBandsToEditableRows,
  pickParentTuitionBandListAggregates,
  parentTuitionBandApi,
  schoolBandsApi,
  schoolWithFiltersApi,
  tuitionBandApi,
  buildCreateSchoolClassificationBasedOnBandsBody,
  schoolClassificationApi,
  type CalcBandSchoolCountItem,
  type CalcSchoolBandTuitionInputItem,
  type NestedTuitionBandDto,
  type ParentBandEditableRow,
  type ParentTuitionBandDto,
  type SchoolBandWithTuitionBandsDto,
  type SchoolByIdsDto,
  type SizeBandEditableRow,
  type TuitionBandEmbeddedSchoolDto,
} from '@/features/reports/api';
import {
  bandNamesAreUnique,
  findOverlappingBandPairIndices,
  hasAnyBandRangeOverlap,
  sortBandRowIndicesByBounds,
} from '@/features/reports/utils/bandOverlap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, primeAbpAntiforgerySession } from '@/shared/api';
import { useManageClassification } from '@/features/reports/classification';
import {
  parentTuitionBandsQueryKeys,
  useParentTuitionBandsQuery,
} from '@/features/reports/hooks/useParentTuitionBandsQuery';
import {
  schoolBandsQueryKeys,
  useSchoolBandsWithMinQuery,
} from '@/features/reports/hooks/useSchoolBandsQuery';
import { CalendarIcon } from '@/shared/ui/Icons';

const STEP_LABELS = [
  'Core parameters',
  'Size bands',
  'Tuition bands',
  'Parent-facing tuition band',
  'Summary & submit',
] as const;

/** Final step: combined distributions + submit. */
const SUMMARY_STEP_INDEX = STEP_LABELS.length - 1;

const OUT_OF_BAND_PAGE_SIZE = 10;
const PARENT_SCHOOL_MODAL_PAGE_SIZE = 20;
const SIZE_BAND_EMBEDDED_PAGE_SIZE = 10;

function parsePositiveMinSchools(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) return null;
  return n;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isPersistedBandId(key: string): boolean {
  return UUID_RE.test(key) && key.toLowerCase() !== CALC_BAND_NEW_ID;
}

/** Per-row name placeholder used only in calc payloads so the server never sees blank/duplicate names; the user's typed value (which may be empty or duplicated) is preserved in state. */
const CALC_PAYLOAD_PLACEHOLDER_PREFIX = '__calc_placeholder__:';

function calcPayloadName(row: SizeBandEditableRow): string {
  const trimmed = row.name.trim();
  if (trimmed) return trimmed;
  return `${CALC_PAYLOAD_PLACEHOLDER_PREFIX}${row.key}`;
}

function isCalcPayloadPlaceholderName(name: string | null | undefined): boolean {
  return typeof name === 'string' && name.startsWith(CALC_PAYLOAD_PLACEHOLDER_PREFIX);
}

function rowToCalcBandPayload(
  row: SizeBandEditableRow,
  min: number,
  year: number
): CalcBandSchoolCountItem {
  return {
    id: isPersistedBandId(row.key) ? row.key : CALC_BAND_NEW_ID,
    name: calcPayloadName(row),
    minValue: row.minValue,
    maxValue: row.maxValue,
    description: row.description ?? '',
    minSchoolsPerBand: min,
    year,
  };
}

/** Sort each school band's tuition bands ascending by amount (`minValue`, then `maxValue`). Stable comparator keeps existing order for equal amounts so user edits don't jump unexpectedly. */
function sortTuitionBandsByAmount(
  bands: SchoolBandWithTuitionBandsDto[] | null | undefined
): SchoolBandWithTuitionBandsDto[] {
  if (!Array.isArray(bands)) return [];
  return bands.map((sb) => {
    const tb = sb.tuitionBands ?? [];
    if (tb.length <= 1) return sb;
    const sorted = tb
      .map((row, originalIndex) => ({ row, originalIndex }))
      .sort((a, b) => {
        const aMin = Number.isFinite(a.row.minValue) ? a.row.minValue : Number.POSITIVE_INFINITY;
        const bMin = Number.isFinite(b.row.minValue) ? b.row.minValue : Number.POSITIVE_INFINITY;
        if (aMin !== bMin) return aMin - bMin;
        const aMax = Number.isFinite(a.row.maxValue) ? a.row.maxValue : Number.POSITIVE_INFINITY;
        const bMax = Number.isFinite(b.row.maxValue) ? b.row.maxValue : Number.POSITIVE_INFINITY;
        if (aMax !== bMax) return aMax - bMax;
        return a.originalIndex - b.originalIndex;
      })
      .map((entry) => entry.row);
    return { ...sb, tuitionBands: sorted };
  });
}

function sizeBandRowToTuitionCalcItem(
  row: SizeBandEditableRow,
  min: number,
  year: number
): CalcSchoolBandTuitionInputItem {
  const item: CalcSchoolBandTuitionInputItem = {
    name: row.name,
    minValue: row.minValue,
    maxValue: row.maxValue,
    minSchoolsPerBand: min,
    year,
  };
  if (isPersistedBandId(row.key)) {
    item.id = row.key;
  }
  return item;
}

function parentRowToCalcBandPayload(
  row: ParentBandEditableRow,
  min: number,
  year: number
): CalcBandSchoolCountItem {
  const persistedId =
    row.serverId && isPersistedBandId(row.serverId)
      ? row.serverId
      : isPersistedBandId(row.key)
        ? row.key
        : null;
  return {
    id: persistedId ?? CALC_BAND_NEW_ID,
    name: row.name,
    minValue: row.minValue,
    maxValue: row.maxValue,
    description: row.description ?? '',
    /** Call site passes current wizard year / min-schools (authoritative for populate). */
    minSchoolsPerBand: min,
    year,
  };
}

function parentEditableRowsToPickDto(rows: ParentBandEditableRow[]): ParentTuitionBandDto[] {
  return rows.map((r) => ({
    name: r.name,
    minValue: r.minValue,
    maxValue: r.maxValue,
    description: r.description,
    minSchoolsPerBand: r.minSchoolsPerBand ?? 0,
    year: r.year ?? 0,
    numberOfSchools: r.numberOfSchools,
    schoolIds: r.schoolIds,
    schools: r.schools,
  }));
}

function nextUniqueParentBandName(existing: ParentBandEditableRow[]): string {
  const used = new Set(
    existing.map((r) => r.name.trim().toLowerCase()).filter((s) => s.length > 0)
  );
  let n = existing.length + 1;
  for (let guard = 0; guard < 10_000; guard += 1) {
    const candidate = `New parent-facing tuition band ${n}`;
    const key = candidate.toLowerCase();
    if (!used.has(key)) return candidate;
    n += 1;
  }
  return `New parent-facing tuition band ${Date.now()}`;
}

function formatSchoolByIdsRow(row: SchoolByIdsDto): {
  label: string;
  code: string;
  students: number;
} {
  const codeRaw =
    row.educationalCenterNumber ?? row.code ?? '';
  return {
    label: String(row.schoolName ?? '—').trim() || '—',
    code: String(codeRaw).trim() || '—',
    students: Number(row.totalStudentCount ?? 0) || 0,
  };
}

/** Size-band schools from GET /school-bands `schools[]` — client search, sort, pagination. */
function SizeBandSchoolsEmbeddedModal({
  open,
  title,
  schools,
  onClose,
}: {
  open: boolean;
  title: string;
  schools: SchoolByIdsDto[];
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [studentsSort, setStudentsSort] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setStudentsSort('desc');
    setPage(1);
  }, [open, title]);

  const filteredSorted = useMemo(() => {
    let list = [...schools];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((row) => {
        const { label, code } = formatSchoolByIdsRow(row);
        const codeCmp = code === '—' ? '' : code.toLowerCase();
        return label.toLowerCase().includes(q) || codeCmp.includes(q);
      });
    }
    list.sort((a, b) => {
      const da = Number(a.totalStudentCount ?? 0) || 0;
      const db = Number(b.totalStudentCount ?? 0) || 0;
      return studentsSort === 'asc' ? da - db : db - da;
    });
    return list;
  }, [schools, search, studentsSort]);

  const pageSlice = useMemo(() => {
    const start = (page - 1) * SIZE_BAND_EMBEDDED_PAGE_SIZE;
    return filteredSorted.slice(start, start + SIZE_BAND_EMBEDDED_PAGE_SIZE);
  }, [filteredSorted, page]);

  const toggleStudentsSort = () => {
    setStudentsSort((s) => (s === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      footer={null}
      width={880}
      destroyOnClose
    >
      <div className="mb-3">
        <Input
          allowClear
          placeholder="Search by school name or code…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full max-w-md"
          aria-label="Search schools by name or code"
        />
      </div>
      <div className="max-h-[min(60vh,28rem)] overflow-auto rounded-lg border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2 whitespace-nowrap">Code</th>
              <th className="px-3 py-2 whitespace-nowrap">
                <button
                  type="button"
                  onClick={toggleStudentsSort}
                  className="inline-flex items-center gap-1 rounded px-1 py-0.5 font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
                  aria-sort={studentsSort === 'asc' ? 'ascending' : 'descending'}
                >
                  Students
                  <span
                    className="text-indigo-600 tabular-nums normal-case"
                    aria-hidden
                  >
                    {studentsSort === 'asc' ? '↑' : '↓'}
                  </span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {pageSlice.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-3 py-6 text-center text-slate-500">
                  No schools match this search.
                </td>
              </tr>
            ) : (
              pageSlice.map((row, idx) => {
                const { label, code, students } = formatSchoolByIdsRow(row);
                return (
                  <tr
                    key={String(row.id || `${label}-${page}-${idx}`)}
                    className="border-b border-slate-100 last:border-0"
                  >
                    <td className="px-3 py-2 text-slate-900">{label}</td>
                    <td className="px-3 py-2 font-mono text-xs tabular-nums text-slate-800">
                      {code}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-slate-800">
                      {students.toLocaleString()}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {filteredSorted.length > 0 && (
        <div className="mt-3 flex justify-end">
          <Pagination
            size="small"
            current={page}
            pageSize={SIZE_BAND_EMBEDDED_PAGE_SIZE}
            total={filteredSorted.length}
            onChange={setPage}
            showSizeChanger={false}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} schools`}
          />
        </div>
      )}
    </Modal>
  );
}

function formatTuitionAmount(n: number) {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatTuitionBandSchoolRow(row: TuitionBandEmbeddedSchoolDto): {
  label: string;
  code: string;
  tuitionUSD: number;
} {
  const codeRaw = row.educationalCenterNumber ?? row.code ?? '';
  return {
    label: String(row.schoolName ?? '—').trim() || '—',
    code: String(codeRaw).trim() || '—',
    tuitionUSD: Number(row.tuitionUSD ?? 0) || 0,
  };
}

function sourceSizeBandSearchBlob(row: TuitionBandEmbeddedSchoolDto): string {
  const parts = [
    row.sourceSizeBandName?.trim() ?? '',
    typeof row.sourceSizeBandStudentMin === 'number' &&
    Number.isFinite(row.sourceSizeBandStudentMin)
      ? String(row.sourceSizeBandStudentMin)
      : '',
    typeof row.sourceSizeBandStudentMax === 'number' &&
    Number.isFinite(row.sourceSizeBandStudentMax)
      ? String(row.sourceSizeBandStudentMax)
      : '',
  ];
  return parts.join(' ').toLowerCase();
}

/** Tuition sub-band schools from calc/rebuild `schools[]` — client search, sort, pagination. */
function TuitionBandSchoolsEmbeddedModal({
  open,
  title,
  schools,
  onClose,
  variant,
}: {
  open: boolean;
  title: string;
  schools: TuitionBandEmbeddedSchoolDto[];
  onClose: () => void;
  /** Extra columns: total students + source size band name and student-count bounds. */
  variant?: 'outsideAllTuitionTiers';
}) {
  const [search, setSearch] = useState('');
  const [tuitionSort, setTuitionSort] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  const outsideMode = variant === 'outsideAllTuitionTiers';
  const colSpan = outsideMode ? 5 : 3;

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setTuitionSort('desc');
    setPage(1);
  }, [open, title, variant]);

  const filteredSorted = useMemo(() => {
    let list = [...schools];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((row) => {
        const { label, code } = formatTuitionBandSchoolRow(row);
        const codeCmp = code === '—' ? '' : code.toLowerCase();
        const bandBlob = outsideMode ? sourceSizeBandSearchBlob(row) : '';
        return (
          label.toLowerCase().includes(q) ||
          codeCmp.includes(q) ||
          bandBlob.includes(q)
        );
      });
    }
    list.sort((a, b) => {
      const da = Number(a.tuitionUSD ?? 0) || 0;
      const db = Number(b.tuitionUSD ?? 0) || 0;
      return tuitionSort === 'asc' ? da - db : db - da;
    });
    return list;
  }, [schools, search, tuitionSort, outsideMode]);

  const pageSlice = useMemo(() => {
    const start = (page - 1) * SIZE_BAND_EMBEDDED_PAGE_SIZE;
    return filteredSorted.slice(start, start + SIZE_BAND_EMBEDDED_PAGE_SIZE);
  }, [filteredSorted, page]);

  const toggleTuitionSort = () => {
    setTuitionSort((s) => (s === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      footer={null}
      width={outsideMode ? 1080 : 920}
      destroyOnClose
    >
      <div className="mb-3">
        <Input
          allowClear
          placeholder={
            outsideMode
              ? 'Search by name, code, or size band…'
              : 'Search by school name or code…'
          }
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full max-w-md"
          aria-label="Search schools"
        />
      </div>
      <div className="max-h-[min(60vh,28rem)] overflow-auto rounded-lg border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2 whitespace-nowrap">Code</th>
              {outsideMode && (
                <th className="px-3 py-2 whitespace-nowrap">Students</th>
              )}
              <th className="px-3 py-2 whitespace-nowrap">
                <button
                  type="button"
                  onClick={toggleTuitionSort}
                  className="inline-flex items-center gap-1 rounded px-1 py-0.5 font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
                  aria-sort={tuitionSort === 'asc' ? 'ascending' : 'descending'}
                >
                  Tuition (USD)
                  <span
                    className="text-indigo-600 tabular-nums normal-case"
                    aria-hidden
                  >
                    {tuitionSort === 'asc' ? '↑' : '↓'}
                  </span>
                </button>
              </th>
              {outsideMode && (
                <th className="min-w-[10rem] px-3 py-2">Size band</th>
              )}
            </tr>
          </thead>
          <tbody>
            {pageSlice.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-3 py-6 text-center text-slate-500">
                  No schools match this search.
                </td>
              </tr>
            ) : (
              pageSlice.map((row, idx) => {
                const { label, code, tuitionUSD } = formatTuitionBandSchoolRow(row);
                const students = Number(row.totalStudentCount ?? 0) || 0;
                return (
                  <tr
                    key={String(row.id || `${label}-${page}-${idx}`)}
                    className="border-b border-slate-100 last:border-0"
                  >
                    <td className="px-3 py-2 text-slate-900">{label}</td>
                    <td className="px-3 py-2 font-mono text-xs tabular-nums text-slate-800">
                      {code}
                    </td>
                    {outsideMode && (
                      <td className="px-3 py-2 tabular-nums text-slate-800">
                        {students.toLocaleString()}
                      </td>
                    )}
                    <td className="px-3 py-2 tabular-nums text-slate-800">
                      {'$'}
                      {formatTuitionAmount(tuitionUSD)}
                    </td>
                    {outsideMode && (
                      <td className="max-w-[14rem] px-3 py-2 text-slate-800">
                        <div className="font-medium text-slate-900">
                          {row.sourceSizeBandName?.trim() || '—'}
                        </div>
                        {typeof row.sourceSizeBandStudentMin === 'number' &&
                        typeof row.sourceSizeBandStudentMax === 'number' &&
                        Number.isFinite(row.sourceSizeBandStudentMin) &&
                        Number.isFinite(row.sourceSizeBandStudentMax) ? (
                          <div className="mt-0.5 text-[11px] font-normal normal-case text-slate-500 tabular-nums">
                            Students{' '}
                            {row.sourceSizeBandStudentMin.toLocaleString()}–
                            {row.sourceSizeBandStudentMax.toLocaleString()}
                          </div>
                        ) : null}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {filteredSorted.length > 0 && (
        <div className="mt-3 flex justify-end">
          <Pagination
            size="small"
            current={page}
            pageSize={SIZE_BAND_EMBEDDED_PAGE_SIZE}
            total={filteredSorted.length}
            onChange={setPage}
            showSizeChanger={false}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} schools`}
          />
        </div>
      )}
    </Modal>
  );
}

function ParentTuitionBandsTable({
  mode,
  rows,
  onRowsChange,
  onAddRow,
  onRemoveRow,
  onOpenSchools,
  onOpenOutsideSchools,
  totalCount,
  totalNumberOfSchools,
  totalNumberOfSchoolsOutsideBands,
  preferDerivedOutsideFromRows = false,
  showParentMarketCoverage = true,
  isPending,
  isFetching,
  errorMessage,
  yearLabel,
  minSchools,
  showChart,
}: {
  mode: 'edit' | 'view';
  rows: ParentBandEditableRow[];
  onRowsChange?: (rows: ParentBandEditableRow[]) => void;
  onAddRow?: () => void;
  onRemoveRow?: (key: string) => void;
  onOpenSchools: (row: ParentBandEditableRow) => void;
  onOpenOutsideSchools?: () => void;
  totalCount: number;
  /** Same on every API row — total schools in the parent-market universe. */
  totalNumberOfSchools?: number | null;
  /** Same on every API row — schools not in any parent band (server). */
  totalNumberOfSchoolsOutsideBands?: number | null;
  /**
   * When true (e.g. unsaved draft), “outside” uses total − sum(row school counts) so deletes/edits
   * to the row list update the figure; when false, prefer the server’s outside count when present.
   */
  preferDerivedOutsideFromRows?: boolean;
  /** When false, hides the “Parent-market coverage” summary (step 4 omits it). */
  showParentMarketCoverage?: boolean;
  isPending: boolean;
  isFetching: boolean;
  errorMessage: string | null;
  yearLabel: string;
  minSchools: number;
  showChart: boolean;
}) {
  const chartData = rows.map((b) => ({
    name: b.name?.trim() || '—',
    schools: b.numberOfSchools ?? 0,
  }));

  const schoolsInParentBands = showParentMarketCoverage
    ? rows.reduce((s, r) => {
        const n = r.numberOfSchools;
        return s + (typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : 0);
      }, 0)
    : 0;
  const schoolsOutsideParentBands = showParentMarketCoverage
    ? (() => {
        const derived =
          totalNumberOfSchools != null && Number.isFinite(totalNumberOfSchools)
            ? Math.max(0, totalNumberOfSchools - schoolsInParentBands)
            : null;
        if (preferDerivedOutsideFromRows && derived != null) return derived;
        if (
          totalNumberOfSchoolsOutsideBands != null &&
          Number.isFinite(totalNumberOfSchoolsOutsideBands)
        ) {
          return totalNumberOfSchoolsOutsideBands;
        }
        return derived;
      })()
    : null;

  const inputClass =
    'w-full min-w-[5rem] rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500';

  const updateRow = (key: string, patch: Partial<ParentBandEditableRow>) => {
    if (!onRowsChange) return;
    onRowsChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Loaded for year <span className="font-semibold tabular-nums text-slate-700">{yearLabel}</span>{' '}
        with minimum{' '}
        <span className="font-semibold tabular-nums text-slate-700">{minSchools}</span> schools per
        band (same as step 1).
        {mode === 'edit' && (
          <>
            {' '}
            Membership counts refresh as you edit boundaries. Band definitions are stored when you
            submit classification on the final step.
          </>
        )}
      </p>
      {errorMessage && (
        <Alert type="error" showIcon message={errorMessage} className="mb-0" />
      )}
      {showParentMarketCoverage &&
        rows.length > 0 &&
        !errorMessage &&
        (totalNumberOfSchools != null || schoolsOutsideParentBands != null) && (
          <div className="rounded-lg border border-violet-100 bg-violet-50/60 px-4 py-3 text-sm text-violet-950">
            <p className="font-medium text-violet-900">Parent-facing tuition band coverage</p>
            <ul className="mt-2 list-inside list-disc space-y-1 text-violet-950/90">
              {totalNumberOfSchools != null && (
                <li>
                  Total schools in market:{' '}
                  <span className="font-semibold tabular-nums">
                    {totalNumberOfSchools.toLocaleString()}
                  </span>
                </li>
              )}
              <li>
                In a parent-facing tuition band:{' '}
                <span className="font-semibold tabular-nums">{schoolsInParentBands.toLocaleString()}</span>
              </li>
              <li>
                Outside parent-facing tuition bands:{' '}
                {onOpenOutsideSchools &&
                schoolsOutsideParentBands != null &&
                schoolsOutsideParentBands > 0 ? (
                  <button
                    type="button"
                    onClick={onOpenOutsideSchools}
                    className="font-semibold tabular-nums text-amber-800 underline decoration-amber-300 underline-offset-2 hover:text-amber-950"
                  >
                    {schoolsOutsideParentBands.toLocaleString()}
                  </button>
                ) : (
                  <span
                    className={`font-semibold tabular-nums ${
                      (schoolsOutsideParentBands ?? 0) > 0 ? 'text-amber-800' : 'text-emerald-800'
                    }`}
                  >
                    {(schoolsOutsideParentBands ?? 0).toLocaleString()}
                  </span>
                )}
              </li>
            </ul>
          </div>
        )}
      {(isPending || isFetching) && !errorMessage && (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Spin size="small" />
          <span>
            {isPending ? 'Loading parent-facing tuition bands…' : 'Refreshing band membership…'}
          </span>
        </div>
      )}
      {mode === 'edit' && onAddRow && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="secondary" onClick={onAddRow} disabled={isPending || isFetching}>
            Add band
          </Button>
        </div>
      )}
      {!isPending && !errorMessage && rows.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-500">
          {mode === 'edit'
            ? 'No parent-facing tuition bands yet. Click “Add band” or adjust filters and reload.'
            : 'No parent-facing tuition bands returned for these parameters.'}
        </div>
      )}
      {!errorMessage && rows.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-700">
                  <th className="px-3 py-3 font-semibold">Band name</th>
                  <th className="px-3 py-3 font-semibold">Min (USD)</th>
                  <th className="px-3 py-3 font-semibold">Max (USD)</th>
                  <th className="px-3 py-3 font-semibold tabular-nums">Schools</th>
                  <th className="px-3 py-3 font-semibold">Description</th>
                  {mode === 'edit' && <th className="px-3 py-3 font-semibold w-[5rem]"> </th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  /** When count > 0, open handler may lazy-fetch membership (GET list often omits schoolIds). */
                  const canOpenSchools = (row.numberOfSchools ?? 0) > 0;
                  return (
                    <tr
                      key={row.key}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50"
                    >
                      <td className="px-3 py-2 align-top">
                        {mode === 'edit' && onRowsChange ? (
                          <input
                            type="text"
                            className={inputClass}
                            value={row.name}
                            onChange={(e) => updateRow(row.key, { name: e.target.value })}
                            placeholder="Name"
                            aria-label="Band name"
                          />
                        ) : (
                          <span className="font-medium text-slate-900">{row.name || '—'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top tabular-nums">
                        {mode === 'edit' && onRowsChange ? (
                          <input
                            type="number"
                            className={`${inputClass} max-w-[7rem]`}
                            value={Number.isFinite(row.minValue) ? row.minValue : ''}
                            onChange={(e) =>
                              updateRow(row.key, { minValue: Number(e.target.value) || 0 })
                            }
                            step="0.01"
                            aria-label="Min USD"
                          />
                        ) : (
                          formatTuitionAmount(row.minValue)
                        )}
                      </td>
                      <td className="px-3 py-2 align-top tabular-nums">
                        {mode === 'edit' && onRowsChange ? (
                          <input
                            type="number"
                            className={`${inputClass} max-w-[7rem]`}
                            value={Number.isFinite(row.maxValue) ? row.maxValue : ''}
                            onChange={(e) =>
                              updateRow(row.key, { maxValue: Number(e.target.value) || 0 })
                            }
                            step="0.01"
                            aria-label="Max USD"
                          />
                        ) : (
                          formatTuitionAmount(row.maxValue)
                        )}
                      </td>
                      <td className="px-3 py-2 align-top tabular-nums">
                        {canOpenSchools ? (
                          <button
                            type="button"
                            onClick={() => void onOpenSchools(row)}
                            className="font-semibold text-violet-700 underline decoration-violet-300 underline-offset-2 hover:text-violet-900"
                          >
                            {(row.numberOfSchools ?? 0).toLocaleString()}
                          </button>
                        ) : (
                          <span className="text-slate-600">
                            {(row.numberOfSchools ?? 0).toLocaleString()}
                          </span>
                        )}
                      </td>
                      <td className="max-w-[12rem] px-3 py-2 align-top text-slate-600">
                        {mode === 'edit' && onRowsChange ? (
                          <input
                            type="text"
                            className={inputClass}
                            value={row.description}
                            onChange={(e) => updateRow(row.key, { description: e.target.value })}
                            placeholder="Optional"
                            aria-label="Description"
                          />
                        ) : (
                          <span>{row.description?.trim() ? row.description : '—'}</span>
                        )}
                      </td>
                      {mode === 'edit' && onRemoveRow && (
                        <td className="px-3 py-2 align-top">
                          <button
                            type="button"
                            onClick={() => onRemoveRow(row.key)}
                            className="text-xs font-semibold text-rose-700 hover:underline"
                          >
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {totalCount > rows.length && (
                <tfoot>
                  <tr className="border-t border-slate-200 bg-slate-50 text-xs text-slate-500">
                    <td colSpan={mode === 'edit' ? 6 : 5} className="px-3 py-2">
                      Showing {rows.length} of {totalCount.toLocaleString()} bands from the server
                      list.
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {showChart && chartData.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h4 className="text-sm font-semibold text-slate-900">Schools per parent-facing tuition band</h4>
              <p className="mb-3 text-xs text-slate-500">
                Count of schools in each national parent-facing tuition band.
              </p>
              <div className="h-[240px] w-full min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 8, left: 4, bottom: 48 }}>
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                      interval={0}
                      angle={-24}
                      textAnchor="end"
                      height={52}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(v: number) => [v.toLocaleString(), 'Schools']}
                      labelFormatter={(l) => String(l)}
                    />
                    <Bar
                      dataKey="schools"
                      name="Schools"
                      fill="#7c3aed"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * School ids assigned to this tuition tier — prefers `schoolIds` when the API sends it
 * (rebuild often refreshes ids before embedded `schools` rows catch up).
 */
function tuitionTierAssignedIds(tb: NestedTuitionBandDto): string[] {
  const fromIds = (tb.schoolIds ?? []).map((x) => String(x).trim()).filter(Boolean);
  if (fromIds.length > 0) return fromIds;
  return (tb.schools ?? []).map((s) => s.id?.trim()).filter((id): id is string => Boolean(id));
}

/** API may omit numberOfSchools; nested bands can expose schoolIds or embedded schools. */
function tuitionSubBandSchoolCount(row: NestedTuitionBandDto): number {
  const n = row.numberOfSchools;
  if (typeof n === 'number' && Number.isFinite(n) && n >= 0) return n;
  const assigned = tuitionTierAssignedIds(row);
  if (assigned.length > 0) return assigned.length;
  const embedded = row.schools;
  if (Array.isArray(embedded) && embedded.length > 0) return embedded.length;
  return 0;
}

function labelNestedTuitionBand(tb: NestedTuitionBandDto, indexInBand: number): string {
  const t = tb.name?.trim();
  if (t) return t;
  return `Tuition band ${indexInBand + 1}`;
}

function totalSchoolsAcrossTuitionBands(tb: NestedTuitionBandDto[]): number {
  return tb.reduce((sum, row) => sum + tuitionSubBandSchoolCount(row), 0);
}

/** Schools listed on the size band but not assigned to any nested tuition band. */
function getSchoolIdsOutsideTuition(sb: SchoolBandWithTuitionBandsDto): string[] {
  const fromRecord = Object.keys(sb.schoolIds ?? {});
  const fromSchools = (sb.schools ?? [])
    .map((s) => s.id?.trim())
    .filter((id): id is string => Boolean(id));
  const parentKeys = [...new Set([...fromRecord, ...fromSchools])];
  const inTuition = new Set<string>();
  for (const tb of sb.tuitionBands ?? []) {
    for (const id of tuitionTierAssignedIds(tb)) inTuition.add(id);
  }
  return parentKeys.filter((id) => !inTuition.has(id)).sort();
}

/** Schools that appear on any size band in the calc tree but not on any nested tuition band’s `schools`. */
function collectSchoolsOutsideAllTuitionTiers(
  bands: SchoolBandWithTuitionBandsDto[] | null | undefined
): TuitionBandEmbeddedSchoolDto[] {
  if (!bands?.length) return [];
  const inAnyTuition = new Set<string>();
  for (const sb of bands) {
    for (const tb of sb.tuitionBands ?? []) {
      for (const id of tuitionTierAssignedIds(tb)) inAnyTuition.add(id);
    }
  }
  const byId = new Map<string, TuitionBandEmbeddedSchoolDto>();
  for (const sb of bands) {
    for (const s of sb.schools ?? []) {
      const id = s.id?.trim();
      if (!id || inAnyTuition.has(id)) continue;
      if (!byId.has(id)) {
        byId.set(id, {
          ...s,
          id,
          sourceSizeBandName: sb.name ?? null,
          sourceSizeBandStudentMin: sb.minValue,
          sourceSizeBandStudentMax: sb.maxValue,
        });
      }
    }
  }
  for (const sb of bands) {
    for (const id of Object.keys(sb.schoolIds ?? {})) {
      const tid = id.trim();
      if (!tid || inAnyTuition.has(tid) || byId.has(tid)) continue;
      const w = sb.schoolIds?.[id];
      const tuitionUSD =
        typeof w === 'number' && Number.isFinite(w) ? w : Number(w) || 0;
      byId.set(tid, {
        id: tid,
        tuitionUSD,
        sourceSizeBandName: sb.name ?? null,
        sourceSizeBandStudentMin: sb.minValue,
        sourceSizeBandStudentMax: sb.maxValue,
      });
    }
  }
  return [...byId.values()];
}

function countSchoolsOutsideTuition(sb: SchoolBandWithTuitionBandsDto): number {
  const explicit = getSchoolIdsOutsideTuition(sb);
  if (explicit.length > 0) return explicit.length;
  const inBand =
    typeof sb.numberOfSchools === 'number' && sb.numberOfSchools >= 0
      ? sb.numberOfSchools
      : Object.keys(sb.schoolIds ?? {}).length;
  const inTuition = totalSchoolsAcrossTuitionBands(sb.tuitionBands ?? []);
  return Math.max(0, inBand - inTuition);
}

/** Not a bare UUID — avoids treating locally added rows as persisted server tuition-band ids on submit. */
function newNestedTuitionBandId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `tmp-nested-${crypto.randomUUID()}`;
  }
  return `tmp-nested-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** One row per tuition band nested under its school (size) band — Y-axis shows name + range. */
type ReviewHeatmapRow = {
  key: string;
  schoolBandIndex: number;
  tuitionIndex: number;
  schoolBandName: string;
  tuitionName: string;
  min: number;
  max: number;
  schoolIds: string[];
};

type ReviewHeatmapGroup = {
  schoolBandIndex: number;
  name: string;
  studentMin: number;
  studentMax: number;
  /** Indices into `rows` / `counts` for tuition bands under this school band. */
  rowIndices: number[];
};

type ReviewHeatmapModel = {
  rows: ReviewHeatmapRow[];
  columns: {
    key: string;
    name: string;
    studentMin: number;
    studentMax: number;
  }[];
  /** null = cell does not apply (tuition band belongs to another size column). */
  counts: (number | null)[][];
  maxCount: number;
  /** One section per school band (size band), each listing its tuition band rows. */
  groups: ReviewHeatmapGroup[];
};

function buildReviewHeatmap(
  bands: SchoolBandWithTuitionBandsDto[] | null | undefined
): ReviewHeatmapModel | null {
  if (!bands?.length) return null;

  const columns = bands.map((sb, i) => ({
    key: sb.id ?? `sb-${i}`,
    name: sb.name ?? `Band ${i + 1}`,
    studentMin: sb.minValue,
    studentMax: sb.maxValue,
  }));

  const rows: ReviewHeatmapRow[] = [];
  bands.forEach((sb, sbIdx) => {
    const tbs = sb.tuitionBands ?? [];
    tbs.forEach((tb, ti) => {
      const ids = tuitionTierAssignedIds(tb);
      rows.push({
        key: `${sbIdx}-${ti}-${tb.id ?? 'tb'}`,
        schoolBandIndex: sbIdx,
        tuitionIndex: ti,
        schoolBandName: sb.name ?? `Size band ${sbIdx + 1}`,
        tuitionName: (tb.name && String(tb.name).trim()) || 'Tuition band',
        min: tb.minValue,
        max: tb.maxValue,
        schoolIds: ids,
      });
    });
  });

  if (!rows.length) return null;

  const counts = rows.map((row) =>
    bands.map((_, ci) => {
      if (ci !== row.schoolBandIndex) return null;
      const tb = bands[ci].tuitionBands?.[row.tuitionIndex];
      if (!tb) return 0;
      return tuitionSubBandSchoolCount(tb);
    })
  );

  let maxCount = 0;
  for (const r of counts) {
    for (const c of r) {
      if (c != null && c > maxCount) maxCount = c;
    }
  }

  const groups: ReviewHeatmapGroup[] = bands.map((_, sbIdx) => {
    const rowIndices: number[] = [];
    rows.forEach((r, ri) => {
      if (r.schoolBandIndex === sbIdx) rowIndices.push(ri);
    });
    const col = columns[sbIdx];
    return {
      schoolBandIndex: sbIdx,
      name: col.name,
      studentMin: col.studentMin,
      studentMax: col.studentMax,
      rowIndices,
    };
  }).filter((g) => g.rowIndices.length > 0);

  return { rows, columns, counts, maxCount, groups };
}

type ReviewSummaryModel = {
  schoolBandCount: number;
  tuitionBandCount: number;
  schoolsAssignedToTuition: number;
  schoolsOutsideTuition: number;
  schoolsInSizeBands: number;
};

function buildReviewSummary(
  bands: SchoolBandWithTuitionBandsDto[] | null | undefined
): ReviewSummaryModel | null {
  if (!bands?.length) return null;
  let schoolsAssignedToTuition = 0;
  let schoolsOutsideTuition = 0;
  let tuitionBandCount = 0;
  let schoolsInSizeBands = 0;
  for (const sb of bands) {
    tuitionBandCount += sb.tuitionBands?.length ?? 0;
    schoolsOutsideTuition += countSchoolsOutsideTuition(sb);
    const inBand =
      typeof sb.numberOfSchools === 'number' && sb.numberOfSchools >= 0
        ? sb.numberOfSchools
        : Object.keys(sb.schoolIds ?? {}).length;
    schoolsInSizeBands += inBand;
    for (const tb of sb.tuitionBands ?? []) {
      schoolsAssignedToTuition += tuitionSubBandSchoolCount(tb);
    }
  }
  return {
    schoolBandCount: bands.length,
    tuitionBandCount,
    schoolsAssignedToTuition,
    schoolsOutsideTuition,
    schoolsInSizeBands,
  };
}

const TUITION_SPECTRUM_PALETTE = [
  'from-amber-400 to-orange-500',
  'from-orange-400 to-rose-500',
  'from-rose-400 to-red-600',
  'from-red-400 to-red-700',
] as const;

/** Proportional “fire” strip: segment width reflects min–max span; legend lists names + ranges. */
function TuitionBandsSpectrum({ bands }: { bands: NestedTuitionBandDto[] }) {
  if (!bands.length) return null;
  const weights = bands.map((b) => {
    const d = Math.abs(Number(b.maxValue) - Number(b.minValue));
    return Number.isFinite(d) && d > 0 ? d : 1;
  });
  let lo = Infinity;
  let hi = -Infinity;
  for (const b of bands) {
    if (Number.isFinite(b.minValue)) lo = Math.min(lo, b.minValue);
    if (Number.isFinite(b.maxValue)) hi = Math.max(hi, b.maxValue);
  }
  const spanOk = Number.isFinite(lo) && Number.isFinite(hi) && lo <= hi;
  const spanLabel = spanOk
    ? `${formatTuitionAmount(lo)} – ${formatTuitionAmount(hi)}`
    : '';

  return (
    <div className="mb-4 space-y-3 rounded-xl border border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-orange-50/30 p-4 shadow-sm ring-1 ring-slate-100">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
          Tuition amount map
        </p>
        {spanLabel && (
          <p className="text-[11px] font-medium tabular-nums text-slate-500">
            Combined span {spanLabel}
          </p>
        )}
      </div>
      <div className="flex h-16 w-full min-h-[4rem] gap-1 overflow-hidden rounded-xl bg-slate-200/40 p-1 shadow-inner ring-1 ring-slate-200/60">
        {bands.map((b, j) => {
          const grad = TUITION_SPECTRUM_PALETTE[j % TUITION_SPECTRUM_PALETTE.length];
          const label = (b.name && String(b.name).trim()) || `Tier ${j + 1}`;
          return (
            <div
              key={`spec-${j}-${b.id ?? j}`}
              className={`relative flex min-w-[2.25rem] flex-col justify-end overflow-hidden rounded-lg bg-gradient-to-b ${grad} px-1 pb-1.5 pt-1 text-center shadow-md ring-1 ring-white/30`}
              style={{ flex: `${weights[j]} 1 0` }}
              title={`${label}: ${formatTuitionAmount(b.minValue)} – ${formatTuitionAmount(b.maxValue)}`}
            >
              <span className="line-clamp-3 text-[10px] font-bold leading-tight text-white drop-shadow">
                {label}
              </span>
            </div>
          );
        })}
      </div>
      <ul className="grid gap-2 sm:grid-cols-2">
        {bands.map((b, j) => {
          const grad = TUITION_SPECTRUM_PALETTE[j % TUITION_SPECTRUM_PALETTE.length];
          const label = (b.name && String(b.name).trim()) || `Tier ${j + 1}`;
          return (
            <li
              key={`leg-${j}-${b.id ?? j}`}
              className="flex items-start gap-2 rounded-lg border border-slate-100 bg-white/90 px-2.5 py-2 text-[11px] text-slate-700 shadow-sm"
            >
              <span
                className={`mt-0.5 h-8 w-1 shrink-0 rounded-full bg-gradient-to-b ${grad}`}
                aria-hidden
              />
              <span className="min-w-0">
                <span className="font-semibold text-slate-900">{label}</span>
                <span className="mt-0.5 block tabular-nums text-slate-500">
                  {formatTuitionAmount(b.minValue)} – {formatTuitionAmount(b.maxValue)}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const SIZE_ENROLLMENT_PALETTE = [
  'from-indigo-500 to-blue-500',
  'from-blue-500 to-cyan-600',
  'from-violet-500 to-indigo-600',
  'from-sky-500 to-blue-600',
] as const;

/** Visual strip of student-count ranges per size band (step 2). */
function SizeBandsEnrollmentSpectrum({ rows }: { rows: SizeBandEditableRow[] }) {
  if (!rows.length) return null;
  const weights = rows.map((r) => {
    const d = Math.abs(Number(r.maxValue) - Number(r.minValue));
    return Number.isFinite(d) && d > 0 ? d : 1;
  });
  let lo = Infinity;
  let hi = -Infinity;
  for (const r of rows) {
    if (Number.isFinite(r.minValue)) lo = Math.min(lo, r.minValue);
    if (Number.isFinite(r.maxValue)) hi = Math.max(hi, r.maxValue);
  }
  const spanOk = Number.isFinite(lo) && Number.isFinite(hi) && lo <= hi;
  const spanLabel = spanOk
    ? `${lo.toLocaleString()} – ${hi.toLocaleString()} students (combined span)`
    : '';

  return (
    <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-white via-indigo-50/40 to-slate-50 p-4 shadow-sm ring-1 ring-indigo-100/60">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-wide text-indigo-900">
          Enrollment map
        </p>
        {spanLabel && (
          <p className="text-[11px] font-medium tabular-nums text-indigo-700/90">
            {spanLabel}
          </p>
        )}
      </div>
      <p className="mb-2 text-xs text-slate-600">
        Segment width follows each band&apos;s student range (max − min). Edit the table below to
        change boundaries.
      </p>
      <div className="flex h-14 w-full min-h-[3.5rem] gap-1 overflow-hidden rounded-xl bg-slate-200/35 p-1 shadow-inner ring-1 ring-slate-200/70">
        {rows.map((r, j) => {
          const grad = SIZE_ENROLLMENT_PALETTE[j % SIZE_ENROLLMENT_PALETTE.length];
          const label = (r.name && String(r.name).trim()) || `Band ${j + 1}`;
          return (
            <div
              key={r.key}
              className={`relative flex min-w-[2rem] flex-col justify-end overflow-hidden rounded-lg bg-gradient-to-b ${grad} px-1 pb-1 pt-1 text-center shadow ring-1 ring-white/40`}
              style={{ flex: `${weights[j]} 1 0` }}
              title={`${label}: ${r.minValue.toLocaleString()}–${r.maxValue.toLocaleString()} students`}
            >
              <span className="line-clamp-2 text-[10px] font-bold leading-tight text-white drop-shadow">
                {label}
              </span>
            </div>
          );
        })}
      </div>
      <ul className="mt-3 grid gap-2 sm:grid-cols-2">
        {rows.map((r, j) => {
          const grad = SIZE_ENROLLMENT_PALETTE[j % SIZE_ENROLLMENT_PALETTE.length];
          const label = (r.name && String(r.name).trim()) || `Band ${j + 1}`;
          return (
            <li
              key={`sz-${r.key}`}
              className="flex items-start gap-2 rounded-lg border border-white/80 bg-white/90 px-2.5 py-2 text-[11px] shadow-sm ring-1 ring-slate-100"
            >
              <span
                className={`mt-0.5 h-7 w-1 shrink-0 rounded-full bg-gradient-to-b ${grad}`}
                aria-hidden
              />
              <span className="min-w-0">
                <span className="font-semibold text-slate-900">{label}</span>
                <span className="mt-0.5 block tabular-nums text-slate-600">
                  {r.minValue.toLocaleString()} – {r.maxValue.toLocaleString()} students
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

type ServerResetCalloutProps = {
  title: string;
  description: string;
  actionLabel: string;
  onAction: () => void | Promise<void>;
  disabled?: boolean;
  pending?: boolean;
};

function ServerResetCallout({
  title,
  description,
  actionLabel,
  onAction,
  disabled,
  pending,
}: ServerResetCalloutProps) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-indigo-200/90 bg-gradient-to-br from-indigo-50/90 via-white to-slate-50 p-4 shadow-md ring-1 ring-indigo-100/70"
      role="region"
      aria-label={title}
    >
      <div
        className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-indigo-300/20"
        aria-hidden
      />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 gap-3 sm:flex-1">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-xl text-white shadow-lg shadow-indigo-600/30">
            <CloudDownloadOutlined aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900">{title}</p>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">{description}</p>
          </div>
        </div>
        <Button
          type="button"
          variant="secondary"
          className="shrink-0 border-indigo-200 bg-white px-5 py-2.5 font-semibold text-indigo-900 shadow-sm ring-1 ring-indigo-100 hover:bg-indigo-50"
          disabled={disabled || pending}
          onClick={() => void onAction()}
        >
          {pending ? 'Working…' : actionLabel}
        </Button>
      </div>
    </div>
  );
}

function reviewHeatmapCellStyle(
  count: number | null,
  maxCount: number,
  interactive: boolean
): CSSProperties {
  if (count === null) {
    return {
      backgroundColor: '#f1f5f9',
      backgroundImage:
        'repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(148,163,184,0.12) 3px, rgba(148,163,184,0.12) 6px)',
      color: '#cbd5e1',
      cursor: 'not-allowed',
    };
  }
  if (maxCount <= 0 || count <= 0) {
    return {
      backgroundColor: '#fffbeb',
      color: '#a8a29e',
      cursor: interactive ? 'pointer' : 'default',
    };
  }
  const t = count / maxCount;
  const h = 48 - t * 48;
  const s = 85 + t * 12;
  const l = 97 - t * 56;
  return {
    backgroundColor: `hsl(${h} ${Math.min(s, 100)}% ${Math.max(l, 28)}%)`,
    color: l < 52 ? '#fffbeb' : '#431407',
    cursor: interactive ? 'pointer' : 'default',
    boxShadow: t > 0.85 ? 'inset 0 0 0 1px rgba(127,29,29,0.35)' : undefined,
  };
}

type OutOfBandModalState =
  | null
  | { type: 'size' }
  | { type: 'tuition'; title: string; schoolIds: string[] }
  | { type: 'heatmap'; title: string; schoolIds: string[] };

export function ManageClassificationPage() {
  const {
    selectedYear,
    setSelectedYear,
    bandsQuery,
    workflow,
    patchWorkflow,
    hasBandsFromApi,
    showNewWorkflowWizard,
    startNewWorkflow,
  } = useManageClassification();

  const queryClient = useQueryClient();

  /** Dev/debug cold loads: ensure antiforgery + cookies are aligned before first POST to /api. */
  useEffect(() => {
    void primeAbpAntiforgerySession();
  }, []);

  const [yearLabel, setYearLabel] = useState('');
  const [minDraft, setMinDraft] = useState('');
  const [showStep1Error, setShowStep1Error] = useState(false);
  const [sizeBandRows, setSizeBandRows] = useState<SizeBandEditableRow[]>([]);
  const [calcPending, setCalcPending] = useState(false);
  const [sizeBandsServerResetPending, setSizeBandsServerResetPending] =
    useState(false);
  const [calcError, setCalcError] = useState<string | null>(null);
  const [totalSchoolOutOfBands, setTotalSchoolOutOfBands] = useState(0);
  const [totalSchoolOutOfBandsIds, setTotalSchoolOutOfBandsIds] = useState<string[]>([]);
  const [outOfBandModal, setOutOfBandModal] = useState<OutOfBandModalState>(null);
  const [outOfBandLoading, setOutOfBandLoading] = useState(false);
  const [outOfBandError, setOutOfBandError] = useState<string | null>(null);
  const [outOfBandItems, setOutOfBandItems] = useState<SchoolByIdsDto[]>([]);
  const [outOfBandTotalCount, setOutOfBandTotalCount] = useState(0);
  const [outOfBandNameSearch, setOutOfBandNameSearch] = useState('');
  const [outOfBandNameSearchDebounced, setOutOfBandNameSearchDebounced] = useState('');
  const [outOfBandStudentsSort, setOutOfBandStudentsSort] = useState<'asc' | 'desc'>('desc');
  const [outOfBandTablePage, setOutOfBandTablePage] = useState(1);
  const calcSeqRef = useRef(0);
  const parentCalcSeqRef = useRef(0);
  const tuitionCalcSeqRef = useRef(0);
  const tuitionRebuildSeqRef = useRef(0);
  /** Last GET /school-bands snapshot applied to step-2 rows — avoids keeping stale rows after refetch. */
  const lastSizeBandsDataUpdatedAtRef = useRef(0);
  const newBandKeyRef = useRef(0);
  const sizeBandRowsRef = useRef(sizeBandRows);
  sizeBandRowsRef.current = sizeBandRows;

  const [tuitionCalcResult, setTuitionCalcResult] = useState<
    SchoolBandWithTuitionBandsDto[] | null
  >(null);
  const [tuitionCalcPending, setTuitionCalcPending] = useState(false);
  const [tuitionCalcError, setTuitionCalcError] = useState<string | null>(null);
  const [rebuildPending, setRebuildPending] = useState(false);
  const [tuitionServerResetPending, setTuitionServerResetPending] =
    useState(false);
  const [submitPending, setSubmitPending] = useState(false);

  const [parentBandRows, setParentBandRows] = useState<ParentBandEditableRow[]>([]);
  const [parentBandsDirty, setParentBandsDirty] = useState(false);
  const [parentBandsCalcPending, setParentBandsCalcPending] = useState(false);
  const [parentBandsCalcError, setParentBandsCalcError] = useState<string | null>(null);
  /** Last successful POST populate-band-membership universe totals (while draft edits are dirty). */
  const [parentCalcTotals, setParentCalcTotals] = useState<{
    totalNumberOfSchools?: number;
    totalNumberOfSchoolsOutsideBands?: number;
  } | null>(null);
  const parentBandRowsRef = useRef(parentBandRows);
  parentBandRowsRef.current = parentBandRows;
  const parentBandNewKeyRef = useRef(0);
  const [parentSchoolModal, setParentSchoolModal] = useState<
    | null
    | { title: string; variant: 'bandMembers'; schoolIds: string[] }
    | { title: string; variant: 'bandMembersEmbedded'; schools: SchoolByIdsDto[] }
    | { title: string; variant: 'parentMarketOutside' }
  >(null);
  const [parentSchoolModalPage, setParentSchoolModalPage] = useState(1);
  const [parentMarketOutsideSearch, setParentMarketOutsideSearch] = useState('');
  const [parentMarketOutsideSearchDebounced, setParentMarketOutsideSearchDebounced] =
    useState('');
  const [parentMarketOutsideStudentsSort, setParentMarketOutsideStudentsSort] = useState<
    'asc' | 'desc'
  >('desc');
  const [parentBandsServerResetPending, setParentBandsServerResetPending] =
    useState(false);
  const [sizeBandSchoolsModal, setSizeBandSchoolsModal] = useState<{
    title: string;
    schools: SchoolByIdsDto[];
  } | null>(null);
  const [tuitionBandSchoolsModal, setTuitionBandSchoolsModal] = useState<{
    title: string;
    schools: TuitionBandEmbeddedSchoolDto[];
    variant?: 'outsideAllTuitionTiers';
  } | null>(null);

  const tuitionCalcResultRef = useRef<SchoolBandWithTuitionBandsDto[] | null>(null);
  tuitionCalcResultRef.current = tuitionCalcResult;

  const fetchYears = useCallback(async (search: string): Promise<SearchableOption[]> => {
    const data = await budgetsApi.getBudgetYears({
      skipCount: 0,
      maxResultCount: 100,
      year: search || undefined,
    });
    return (data ?? []).map((item) => ({
      id: String(item),
      label: String(item),
    }));
  }, []);

  useEffect(() => {
    setYearLabel(selectedYear);
  }, [selectedYear]);

  const stepIndex = workflow?.stepIndex ?? 0;

  const modalSchoolIds = useMemo(() => {
    if (!outOfBandModal) return [];
    if (outOfBandModal.type === 'size') return totalSchoolOutOfBandsIds;
    return outOfBandModal.schoolIds;
  }, [outOfBandModal, totalSchoolOutOfBandsIds]);

  const sizeBandsQuery = useSchoolBandsWithMinQuery(
    workflow?.year,
    workflow?.minSchoolsPerBand,
    { enabled: stepIndex === 1 && workflow != null }
  );

  const parentBandsQuery = useParentTuitionBandsQuery(workflow?.year, workflow?.minSchoolsPerBand, {
    enabled:
      !!workflow &&
      (stepIndex === 3 || stepIndex === SUMMARY_STEP_INDEX) &&
      workflow.minSchoolsPerBand != null &&
      workflow.minSchoolsPerBand > 0,
  });

  const parentBandsErrorMsg = useMemo(() => {
    if (!parentBandsQuery.isError) return null;
    const e = parentBandsQuery.error;
    if (e instanceof ApiError) return ApiError.getUserMessage(e) ?? e.message;
    return e instanceof Error ? e.message : 'Could not load parent-facing tuition bands.';
  }, [parentBandsQuery.isError, parentBandsQuery.error]);

  /** Schools outside all parent bands from last populate-band-membership (draft step). */
  const [parentPopulateOutsideSchools, setParentPopulateOutsideSchools] = useState<
    SchoolByIdsDto[] | null
  >(null);

  useEffect(() => {
    if (!parentBandsDirty) {
      setParentCalcTotals(null);
      setParentPopulateOutsideSchools(null);
    }
  }, [parentBandsDirty]);

  const parentListAggregates = useMemo(
    () => pickParentTuitionBandListAggregates(parentBandsQuery.data?.items ?? []),
    [parentBandsQuery.data?.items]
  );

  const summaryParentBandRows = useMemo(() => {
    if (stepIndex === SUMMARY_STEP_INDEX && parentBandsDirty) {
      return parentBandRows;
    }
    return mapParentTuitionBandsToEditableRows(parentBandsQuery.data?.items ?? []);
  }, [
    stepIndex,
    parentBandsDirty,
    parentBandRows,
    parentBandsQuery.data?.items,
  ]);

  const summaryParentListAggregates = useMemo(() => {
    if (stepIndex !== SUMMARY_STEP_INDEX) return parentListAggregates;
    if (parentBandsDirty) {
      const picked = pickParentTuitionBandListAggregates(
        parentEditableRowsToPickDto(parentBandRows)
      );
      if (parentCalcTotals != null) {
        return {
          totalNumberOfSchools:
            parentCalcTotals.totalNumberOfSchools ?? picked.totalNumberOfSchools,
          totalNumberOfSchoolsOutsideBands:
            parentCalcTotals.totalNumberOfSchoolsOutsideBands ??
            picked.totalNumberOfSchoolsOutsideBands,
        };
      }
      return picked;
    }
    return parentListAggregates;
  }, [
    stepIndex,
    parentBandsDirty,
    parentBandRows,
    parentCalcTotals,
    parentListAggregates,
  ]);

  const summaryParentPieData = useMemo(() => {
    if (stepIndex !== SUMMARY_STEP_INDEX || !summaryParentBandRows.length) return [];
    const inBands = summaryParentBandRows.reduce((s, r) => {
      const n = r.numberOfSchools;
      return s + (typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : 0);
    }, 0);
    const outside =
      typeof summaryParentListAggregates.totalNumberOfSchoolsOutsideBands === 'number' &&
      Number.isFinite(summaryParentListAggregates.totalNumberOfSchoolsOutsideBands)
        ? Math.max(0, summaryParentListAggregates.totalNumberOfSchoolsOutsideBands)
        : 0;
    const total = summaryParentListAggregates.totalNumberOfSchools;
    if (typeof total !== 'number' || !Number.isFinite(total) || total <= 0) return [];
    const inCapped = Math.min(inBands, total);
    const outCapped = Math.min(outside, Math.max(0, total - inCapped));
    const rest = Math.max(0, total - inCapped - outCapped);
    const segments: { name: string; value: number; fill: string }[] = [];
    if (inCapped > 0) segments.push({ name: 'In parent-facing tuition band(s)', value: inCapped, fill: '#7c3aed' });
    if (outCapped > 0) {
      segments.push({ name: 'Outside parent-facing tuition bands', value: outCapped, fill: '#f59e0b' });
    }
    if (rest > 0) {
      segments.push({ name: 'Unassigned in view', value: rest, fill: '#94a3b8' });
    }
    return segments.length ? segments : [{ name: 'In parent-facing tuition band(s)', value: total, fill: '#7c3aed' }];
  }, [stepIndex, summaryParentBandRows, summaryParentListAggregates]);

  const parentMarketOutsideCount = useMemo(() => {
    const pickedRows = pickParentTuitionBandListAggregates(
      parentEditableRowsToPickDto(parentBandRows)
    );
    if (parentBandsDirty) {
      const o =
        parentCalcTotals?.totalNumberOfSchoolsOutsideBands ??
        pickedRows.totalNumberOfSchoolsOutsideBands;
      if (typeof o === 'number' && Number.isFinite(o)) return o;
      return undefined;
    }
    const o = parentListAggregates.totalNumberOfSchoolsOutsideBands;
    if (typeof o === 'number' && Number.isFinite(o)) return o;
    return undefined;
  }, [parentBandsDirty, parentCalcTotals, parentBandRows, parentListAggregates]);

  const parentMarketTotalSchools = useMemo(() => {
    const pickedRows = pickParentTuitionBandListAggregates(
      parentEditableRowsToPickDto(parentBandRows)
    );
    if (parentBandsDirty) {
      const t =
        parentCalcTotals?.totalNumberOfSchools ?? pickedRows.totalNumberOfSchools;
      if (typeof t === 'number' && Number.isFinite(t)) return t;
      return undefined;
    }
    const t = parentListAggregates.totalNumberOfSchools;
    if (typeof t === 'number' && Number.isFinite(t)) return t;
    return undefined;
  }, [parentBandsDirty, parentCalcTotals, parentBandRows, parentListAggregates]);

  const parentStepSchoolsInBands = useMemo(
    () =>
      parentBandRows.reduce((s, r) => {
        const n = r.numberOfSchools;
        return s + (typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : 0);
      }, 0),
    [parentBandRows]
  );

  const parentBandBoundsSignature = useMemo(
    () =>
      JSON.stringify(
        parentBandRows.map((r) => ({
          min: r.minValue,
          max: r.maxValue,
        }))
      ),
    [parentBandRows]
  );

  useEffect(() => {
    if (stepIndex === 3) return;
    parentCalcSeqRef.current += 1;
    setParentBandsCalcPending(false);
    setParentBandsCalcError(null);
    setParentPopulateOutsideSchools(null);
  }, [stepIndex]);

  /** Debounced POST parent-tuition-band/populate-band-membership when draft USD boundaries change. */
  useEffect(() => {
    if (stepIndex !== 3) return;
    if (!parentBandsDirty) return;
    if (!parentBandRows.length) return;
    const min = workflow?.minSchoolsPerBand;
    const year = Number(workflow?.year);
    if (min == null || min <= 0 || !Number.isFinite(year)) return;
    if (
      parentBandRows.some(
        (r) =>
          !Number.isFinite(r.minValue) ||
          !Number.isFinite(r.maxValue) ||
          r.minValue > r.maxValue
      )
    ) {
      return;
    }

    const seq = ++parentCalcSeqRef.current;
    const t = window.setTimeout(() => {
      void (async () => {
        setParentBandsCalcPending(true);
        setParentBandsCalcError(null);
        try {
          const rows = parentBandRowsRef.current;
          const order = sortBandRowIndicesByBounds(rows);
          const body: CalcBandSchoolCountItem[] = order.map((origIdx) =>
            parentRowToCalcBandPayload(rows[origIdx], min, year)
          );
          const result = await parentTuitionBandApi.populateBandMembership(year, body);
          if (seq !== parentCalcSeqRef.current) return;
          const bands = result.bands;
          const picked = pickParentTuitionBandListAggregates(bands);
          const sumInBands = bands.reduce((s, b) => {
            const n = b.numberOfSchools;
            return s + (typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : 0);
          }, 0);
          const tns = result.totalNumberOfSchools ?? picked.totalNumberOfSchools;
          let tnsob =
            result.totalNumberOfSchoolsOutsideBands ??
            picked.totalNumberOfSchoolsOutsideBands;
          const totalKnown =
            typeof tns === 'number' && Number.isFinite(tns) && tns >= 0 ? tns : null;
          if (
            (tnsob === undefined || !Number.isFinite(tnsob)) &&
            totalKnown != null
          ) {
            tnsob = Math.max(0, totalKnown - sumInBands);
          } else if (
            (tnsob === undefined || !Number.isFinite(tnsob)) &&
            sumInBands > 0 &&
            totalKnown == null
          ) {
            tnsob = 0;
          }
          const finalOutside =
            typeof tnsob === 'number' && Number.isFinite(tnsob)
              ? tnsob
              : totalKnown != null
                ? Math.max(0, totalKnown - sumInBands)
                : 0;
          setParentCalcTotals({
            totalNumberOfSchools:
              totalKnown != null
                ? totalKnown
                : sumInBands > 0
                  ? sumInBands
                  : picked.totalNumberOfSchools,
            totalNumberOfSchoolsOutsideBands: finalOutside,
          });
          setParentPopulateOutsideSchools(result.schoolsOutsideBands);
          setParentBandRows((prev) => {
            if (bands.length !== prev.length) return prev;
            return prev.map((r, origIdx) => {
              const sortedPos = order.findIndex((oi) => oi === origIdx);
              const b = sortedPos >= 0 ? bands[sortedPos] : undefined;
              if (!b) return r;
              const bid = b.id?.trim();
              const nextServerId =
                bid && isPersistedBandId(bid) ? bid : r.serverId;
              const nextKey =
                nextServerId && isPersistedBandId(nextServerId) ? nextServerId : r.key;
              return {
                ...r,
                key: nextKey,
                serverId: nextServerId,
                numberOfSchools: b.numberOfSchools ?? r.numberOfSchools,
                schoolIds: b.schoolIds ?? r.schoolIds,
                schools: b.schools?.length ? b.schools : undefined,
              };
            });
          });
        } catch (e) {
          if (seq !== parentCalcSeqRef.current) return;
          setParentPopulateOutsideSchools(null);
          setParentBandsCalcError(
            e instanceof ApiError
              ? ApiError.getUserMessage(e) ??
                  'Could not populate parent-facing tuition band membership.'
              : 'Could not populate parent-facing tuition band membership.'
          );
        } finally {
          if (seq === parentCalcSeqRef.current) setParentBandsCalcPending(false);
        }
      })();
    }, 400);

    return () => clearTimeout(t);
  }, [
    parentBandBoundsSignature,
    stepIndex,
    parentBandsDirty,
    workflow?.minSchoolsPerBand,
    workflow?.year,
  ]);

  useEffect(() => {
    if (stepIndex !== 3) return;
    const items = parentBandsQuery.data?.items;
    if (!items || parentBandsDirty) return;
    setParentBandRows(mapParentTuitionBandsToEditableRows(items));
  }, [stepIndex, parentBandsQuery.data, parentBandsQuery.dataUpdatedAt, parentBandsDirty]);

  useEffect(() => {
    setParentSchoolModalPage(1);
  }, [
    parentSchoolModal?.title,
    parentSchoolModal?.variant,
    parentSchoolModal?.variant === 'bandMembers'
      ? parentSchoolModal.schoolIds.join('|')
      : parentSchoolModal?.variant === 'bandMembersEmbedded'
        ? parentSchoolModal.schools.map((s) => s.id).join('|')
        : '',
  ]);

  const parentOutsideUsesEmbeddedList = useMemo(
    () =>
      parentSchoolModal?.variant === 'parentMarketOutside' &&
      parentBandsDirty &&
      parentPopulateOutsideSchools != null,
    [parentSchoolModal, parentBandsDirty, parentPopulateOutsideSchools]
  );

  const parentOutsideEmbeddedPage = useMemo(() => {
    if (!parentOutsideUsesEmbeddedList || parentPopulateOutsideSchools == null) {
      return { items: [] as SchoolByIdsDto[], total: 0 };
    }
    let list = [...parentPopulateOutsideSchools];
    const q = parentMarketOutsideSearchDebounced.trim().toLowerCase();
    if (q) {
      list = list.filter((row) => {
        const { label, code } = formatSchoolByIdsRow(row);
        const codeCmp = code === '—' ? '' : code.toLowerCase();
        return (
          label.toLowerCase().includes(q) ||
          codeCmp.includes(q) ||
          String(row.id ?? '')
            .toLowerCase()
            .includes(q)
        );
      });
    }
    list.sort((a, b) => {
      const sa = Number(a.totalStudentCount ?? 0);
      const sb = Number(b.totalStudentCount ?? 0);
      return parentMarketOutsideStudentsSort === 'asc' ? sa - sb : sb - sa;
    });
    const total = list.length;
    const start = (parentSchoolModalPage - 1) * PARENT_SCHOOL_MODAL_PAGE_SIZE;
    const items = list.slice(start, start + PARENT_SCHOOL_MODAL_PAGE_SIZE);
    return { items, total };
  }, [
    parentOutsideUsesEmbeddedList,
    parentPopulateOutsideSchools,
    parentMarketOutsideSearchDebounced,
    parentMarketOutsideStudentsSort,
    parentSchoolModalPage,
  ]);

  const parentBandEmbeddedPage = useMemo(() => {
    if (parentSchoolModal?.variant !== 'bandMembersEmbedded') {
      return { items: [] as SchoolByIdsDto[], total: 0 };
    }
    const list = parentSchoolModal.schools;
    const total = list.length;
    const start = (parentSchoolModalPage - 1) * PARENT_SCHOOL_MODAL_PAGE_SIZE;
    return {
      items: list.slice(start, start + PARENT_SCHOOL_MODAL_PAGE_SIZE),
      total,
    };
  }, [parentSchoolModal, parentSchoolModalPage]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = parentMarketOutsideSearch.trim();
      setParentMarketOutsideSearchDebounced((prev) => {
        if (prev !== next) setParentSchoolModalPage(1);
        return next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [parentMarketOutsideSearch]);

  const parentSchoolListQuery = useQuery({
    queryKey: [
      'schoolWithFilters',
      'post',
      parentSchoolModal?.variant ?? '',
      parentSchoolModal?.variant === 'bandMembers'
        ? parentSchoolModal.schoolIds.slice().sort().join(',')
        : parentSchoolModal?.variant === 'bandMembersEmbedded'
          ? 'embedded'
          : 'outside',
      parentSchoolModal?.variant === 'parentMarketOutside'
        ? parentMarketOutsideSearchDebounced
        : '',
      parentSchoolModal?.variant === 'parentMarketOutside' ? parentMarketOutsideStudentsSort : '',
      parentSchoolModalPage,
    ],
    queryFn: () => {
      const m = parentSchoolModal!;
      const skip = (parentSchoolModalPage - 1) * PARENT_SCHOOL_MODAL_PAGE_SIZE;
      if (m.variant === 'parentMarketOutside') {
        return schoolWithFiltersApi.postList({
          schoolIds: [],
          getOutsideIds: true,
          skipCount: skip,
          maxResultCount: PARENT_SCHOOL_MODAL_PAGE_SIZE,
          keyword: parentMarketOutsideSearchDebounced || undefined,
          sorting: 'TotalStudentCount',
          dir: parentMarketOutsideStudentsSort,
        });
      }
      if (m.variant === 'bandMembers') {
        return schoolWithFiltersApi.postList({
          schoolIds: m.schoolIds,
          getOutsideIds: false,
          skipCount: skip,
          maxResultCount: PARENT_SCHOOL_MODAL_PAGE_SIZE,
        });
      }
      return Promise.resolve({ items: [], totalCount: 0 });
    },
    enabled:
      !!parentSchoolModal &&
      parentSchoolModal.variant !== 'bandMembersEmbedded' &&
      !(
        parentSchoolModal.variant === 'parentMarketOutside' &&
        parentBandsDirty &&
        parentPopulateOutsideSchools != null
      ) &&
      (parentSchoolModal.variant === 'parentMarketOutside' ||
        (parentSchoolModal.variant === 'bandMembers' && parentSchoolModal.schoolIds.length > 0)),
  });

  const parentModalEmbeddedBand =
    parentSchoolModal?.variant === 'bandMembersEmbedded';
  const parentModalQueryLoading =
    !parentOutsideUsesEmbeddedList &&
    !parentModalEmbeddedBand &&
    (parentSchoolListQuery.isPending || parentSchoolListQuery.isFetching);
  const parentModalDisplayItems = parentOutsideUsesEmbeddedList
    ? parentOutsideEmbeddedPage.items
    : parentModalEmbeddedBand
      ? parentBandEmbeddedPage.items
      : (parentSchoolListQuery.data?.items ?? []);
  const parentModalDisplayTotal = parentOutsideUsesEmbeddedList
    ? parentOutsideEmbeddedPage.total
    : parentModalEmbeddedBand
      ? parentBandEmbeddedPage.total
      : (parentSchoolListQuery.data?.totalCount ?? 0);

  useEffect(() => {
    setMinDraft(
      workflow?.minSchoolsPerBand != null ? String(workflow.minSchoolsPerBand) : ''
    );
    setShowStep1Error(false);
  }, [workflow?.year, workflow?.minSchoolsPerBand]);

  /** Reset size-band editor when year or minimum changes (new API result). */
  useEffect(() => {
    lastSizeBandsDataUpdatedAtRef.current = 0;
    setSizeBandRows([]);
    setTotalSchoolOutOfBandsIds([]);
  }, [selectedYear, workflow?.minSchoolsPerBand]);

  /** Load step-2 rows from GET …?Year&MinSchoolsPerBand; if none, seed one editable band (min 1 required). */
  useEffect(() => {
    if (stepIndex !== 1) return;
    if (!sizeBandsQuery.isSuccess || !sizeBandsQuery.data) return;
    const items = sizeBandsQuery.data.items ?? [];
    const updatedAt = sizeBandsQuery.dataUpdatedAt ?? 0;
    setSizeBandRows((prev) => {
      const serverHasRows = items.length > 0;
      const newerServerSnapshot = updatedAt > lastSizeBandsDataUpdatedAtRef.current;
      const shouldApplyServerRows =
        serverHasRows && (prev.length === 0 || newerServerSnapshot);
      if (shouldApplyServerRows) {
        lastSizeBandsDataUpdatedAtRef.current = updatedAt;
        return mapSchoolBandsToEditableRows(items);
      }
      if (!serverHasRows && prev.length === 0) {
        return [
          {
            key: `new-${++newBandKeyRef.current}`,
            name: '',
            minValue: 0,
            maxValue: 0,
            description: '',
            numberOfSchools: 0,
            schoolIds: {},
          },
        ];
      }
      return prev;
    });
  }, [stepIndex, sizeBandsQuery.isSuccess, sizeBandsQuery.data, sizeBandsQuery.dataUpdatedAt]);

  /** totalSchoolOutOfBands is identical on every band item from GET. */
  useEffect(() => {
    if (!sizeBandsQuery.data?.items?.length) {
      setTotalSchoolOutOfBands(0);
      return;
    }
    setTotalSchoolOutOfBands(sizeBandsQuery.data.items[0]?.totalSchoolOutOfBands ?? 0);
  }, [sizeBandsQuery.data]);

  /** Only name / min / max / description — avoids loop when we only update numberOfSchools from calc. */
  const sizeBoundsSignature = useMemo(
    () =>
      JSON.stringify(
        sizeBandRows.map((r) => ({
          n: r.name,
          min: r.minValue,
          max: r.maxValue,
          d: r.description,
        }))
      ),
    [sizeBandRows]
  );

  /** Same school bands as step 2 — drives tuition calc POST body. */
  const tuitionInputSignature = useMemo(
    () =>
      JSON.stringify({
        rows: sizeBandRows.map((r) => ({
          n: r.name,
          min: r.minValue,
          max: r.maxValue,
        })),
        min: workflow?.minSchoolsPerBand,
        year: workflow?.year,
      }),
    [sizeBandRows, workflow?.minSchoolsPerBand, workflow?.year]
  );

  /**
   * Tuition band definitions only (no school counts / ids) — changes here trigger debounced
   * rebuild-schools-for-bands.
   */
  const tuitionRebuildSignature = useMemo(() => {
    if (!tuitionCalcResult?.length) return '';
    try {
      return JSON.stringify(
        tuitionCalcResult.map((sb) => ({
          n: sb.name,
          sbMin: sb.minValue,
          sbMax: sb.maxValue,
          tuition: (sb.tuitionBands ?? []).map((t, idx) => ({
            i: idx,
            name: t.name,
            min: t.minValue,
            max: t.maxValue,
          })),
        }))
      );
    } catch {
      return '';
    }
  }, [tuitionCalcResult]);

  /** Keeps “schools outside all size bands” in sync with the server after tuition work (same calc as step 2). */
  const syncOutOfBandTotalsFromServer = useCallback(async () => {
    const min = workflow?.minSchoolsPerBand;
    const year = Number(workflow?.year);
    if (min == null || min <= 0 || !Number.isFinite(year)) return;
    const rows = sizeBandRowsRef.current;
    if (!rows.length) return;
    if (
      rows.some(
        (r) =>
          !Number.isFinite(r.minValue) ||
          !Number.isFinite(r.maxValue) ||
          r.minValue > r.maxValue
      )
    ) {
      return;
    }
    try {
      const order = sortBandRowIndicesByBounds(rows);
      const body: CalcBandSchoolCountItem[] = order.map((origIdx) =>
        rowToCalcBandPayload(rows[origIdx], min, year)
      );
      const result = await schoolBandsApi.calcBandSchoolCount(body);
      setTotalSchoolOutOfBands(result.totalSchoolOutOfBands ?? 0);
      setTotalSchoolOutOfBandsIds(result.totalSchoolOutOfBandsIds ?? []);
    } catch {
      /* leave previous totals */
    }
  }, [workflow?.minSchoolsPerBand, workflow?.year]);

  const executeTuitionRebuild = useCallback(
    async (seq: number) => {
      const body = tuitionCalcResultRef.current;
      if (!body?.length) {
        if (seq === tuitionRebuildSeqRef.current) setRebuildPending(false);
        return;
      }
      setRebuildPending(true);
      setTuitionCalcError(null);
      try {
        const result = await tuitionBandApi.rebuildSchoolsForBands(body);
        if (seq !== tuitionRebuildSeqRef.current) return;
        setTuitionCalcResult(sortTuitionBandsByAmount(result ?? []));
        await syncOutOfBandTotalsFromServer();
      } catch (e) {
        if (seq !== tuitionRebuildSeqRef.current) return;
        setTuitionCalcError(
          e instanceof ApiError
            ? ApiError.getUserMessage(e) ?? 'Could not rebuild school assignments.'
            : 'Could not rebuild school assignments.'
        );
      } finally {
        if (seq === tuitionRebuildSeqRef.current) setRebuildPending(false);
      }
    },
    [syncOutOfBandTotalsFromServer]
  );

  /** Debounced POST …/calc-band-school-count when band boundaries change. */
  useEffect(() => {
    if (stepIndex !== 1) return;
    if (!sizeBandRows.length) return;
    const min = workflow?.minSchoolsPerBand;
    const year = Number(workflow?.year);
    if (min == null || min <= 0 || !Number.isFinite(year)) return;

    const seq = ++calcSeqRef.current;
    const t = window.setTimeout(() => {
      (async () => {
        setCalcPending(true);
        setCalcError(null);
        try {
          const rows = sizeBandRowsRef.current;
          /**
           * Only abort on numerically-invalid rows. Empty / duplicate names are allowed for the
           * calc payload (we substitute a unique placeholder in `rowToCalcBandPayload`) so school
           * counts keep updating live as the user types min/max even before naming bands. The
           * Next/Save gate (`step2CanProceed`) still enforces non-empty + unique names separately.
           */
          if (
            rows.some(
              (r) =>
                !Number.isFinite(r.minValue) ||
                !Number.isFinite(r.maxValue) ||
                r.minValue > r.maxValue
            )
          ) {
            return;
          }
          /** Server overlap logic usually follows ascending min; send bands in that order and map back. */
          const order = sortBandRowIndicesByBounds(rows);
          const body: CalcBandSchoolCountItem[] = order.map((origIdx) =>
            rowToCalcBandPayload(rows[origIdx], min, year)
          );
          const result = await schoolBandsApi.calcBandSchoolCount(body);
          if (seq !== calcSeqRef.current) return;
          const bands = result.bands ?? [];
          setTotalSchoolOutOfBands(result.totalSchoolOutOfBands ?? 0);
          setTotalSchoolOutOfBandsIds(result.totalSchoolOutOfBandsIds ?? []);
          setSizeBandRows((prev) => {
            if (bands.length !== prev.length) {
              return bands.map((b, sortedPos) => {
                const origIdx = order[sortedPos];
                const prevRow = prev[origIdx];
                /** Strip the calc-only placeholder name so the user keeps seeing their (possibly empty) value. */
                const echoedName = isCalcPayloadPlaceholderName(b.name) ? undefined : b.name;
                return {
                  key:
                    b?.id && b.id.toLowerCase() !== CALC_BAND_NEW_ID
                      ? b.id
                      : prevRow?.key ?? `band-${origIdx}`,
                  name: prevRow?.name ?? echoedName ?? '',
                  minValue: b.minValue,
                  maxValue: b.maxValue,
                  description: b.description ?? prevRow?.description ?? '',
                  numberOfSchools: b.numberOfSchools ?? 0,
                  schoolIds: b.schoolIds ?? prevRow?.schoolIds,
                  schools: b.schools ?? prevRow?.schools,
                };
              });
            }
            return prev.map((r, origIdx) => {
              const sortedPos = order.findIndex((oi) => oi === origIdx);
              const b = sortedPos >= 0 ? bands[sortedPos] : undefined;
              if (!b) return r;
              const serverId = b?.id;
              const nextKey =
                serverId && serverId.toLowerCase() !== CALC_BAND_NEW_ID
                  ? serverId
                  : r.key;
              return {
                ...r,
                key: nextKey,
                numberOfSchools: b?.numberOfSchools ?? r.numberOfSchools,
                schoolIds: b?.schoolIds ?? r.schoolIds,
                schools: b?.schools ?? r.schools,
              };
            });
          });
        } catch (e) {
          if (seq !== calcSeqRef.current) return;
          setCalcError(
            e instanceof ApiError
              ? ApiError.getUserMessage(e) ?? 'Recalculation failed.'
              : 'Recalculation failed.'
          );
        } finally {
          if (seq === calcSeqRef.current) setCalcPending(false);
        }
      })();
    }, 400);

    return () => clearTimeout(t);
  }, [sizeBoundsSignature, stepIndex, workflow?.minSchoolsPerBand, workflow?.year]);

  /** Restore size bands from saved draft when opening tuition / review after refresh. */
  useEffect(() => {
    if ((stepIndex !== 2 && stepIndex !== 3 && stepIndex !== SUMMARY_STEP_INDEX) || sizeBandRows.length > 0)
      return;
    const raw = workflow?.sizeBandsDraft;
    if (!raw?.trim()) return;
    try {
      const parsed = JSON.parse(raw) as SizeBandEditableRow[];
      if (Array.isArray(parsed) && parsed.length > 0) setSizeBandRows(parsed);
    } catch {
      /* ignore */
    }
  }, [stepIndex, workflow?.sizeBandsDraft, sizeBandRows.length]);

  /** POST …/calc-school-band-tuition-bands on tuition step. */
  useEffect(() => {
    if (stepIndex !== 2) return;
    if (!sizeBandRows.length) return;
    const min = workflow?.minSchoolsPerBand;
    const year = Number(workflow?.year);
    if (min == null || min <= 0 || !Number.isFinite(year)) return;

    const invalidRow = sizeBandRows.some(
      (r) =>
        r.name.trim() === '' ||
        !Number.isFinite(r.minValue) ||
        !Number.isFinite(r.maxValue) ||
        r.minValue > r.maxValue
    );
    if (invalidRow) {
      setTuitionCalcError(null);
      setTuitionCalcResult(null);
      setTuitionCalcPending(false);
      return;
    }

    const seq = ++tuitionCalcSeqRef.current;
    const t = window.setTimeout(() => {
      (async () => {
        setTuitionCalcPending(true);
        setTuitionCalcError(null);
        try {
          const rows = sizeBandRowsRef.current;
          const body: CalcSchoolBandTuitionInputItem[] = rows.map((r) =>
            sizeBandRowToTuitionCalcItem(r, min, year)
          );
          const result = await tuitionBandApi.calcSchoolBandTuitionBands(body);
          if (seq !== tuitionCalcSeqRef.current) return;
          setTuitionCalcResult(sortTuitionBandsByAmount(result ?? []));
          await syncOutOfBandTotalsFromServer();
        } catch (e) {
          if (seq !== tuitionCalcSeqRef.current) return;
          setTuitionCalcError(
            e instanceof ApiError
              ? ApiError.getUserMessage(e) ?? 'Tuition calculation failed.'
              : 'Tuition calculation failed.'
          );
          setTuitionCalcResult(null);
        } finally {
          if (seq === tuitionCalcSeqRef.current) setTuitionCalcPending(false);
        }
      })();
    }, 400);

    return () => clearTimeout(t);
  }, [
    tuitionInputSignature,
    stepIndex,
    workflow?.minSchoolsPerBand,
    workflow?.year,
    syncOutOfBandTotalsFromServer,
  ]);

  /** Debounced POST …/rebuild-schools-for-bands when tuition boundaries or band list changes. */
  useEffect(() => {
    if (stepIndex !== 2) return;
    if (tuitionCalcPending) return;
    const rows = tuitionCalcResultRef.current;
    if (!rows?.length) return;
    if (!tuitionRebuildSignature) return;

    const invalid = rows.some((sb) =>
      (sb.tuitionBands ?? []).some(
        (tb) =>
          !Number.isFinite(tb.minValue) ||
          !Number.isFinite(tb.maxValue) ||
          tb.minValue > tb.maxValue
      )
    );
    if (invalid) return;

    const seq = ++tuitionRebuildSeqRef.current;
    const t = window.setTimeout(() => {
      void executeTuitionRebuild(seq);
    }, 400);

    return () => clearTimeout(t);
  }, [
    tuitionRebuildSignature,
    stepIndex,
    tuitionCalcPending,
    executeTuitionRebuild,
  ]);

  /** Restore tuition calc snapshot on review step after refresh. */
  useEffect(() => {
    if (stepIndex !== SUMMARY_STEP_INDEX || tuitionCalcResult != null) return;
    const raw = workflow?.tuitionBandsDraft;
    if (!raw?.trim()) return;
    try {
      const parsed = JSON.parse(raw) as SchoolBandWithTuitionBandsDto[];
      if (Array.isArray(parsed) && parsed.length > 0) setTuitionCalcResult(sortTuitionBandsByAmount(parsed));
    } catch {
      /* ignore */
    }
  }, [stepIndex, workflow?.tuitionBandsDraft, tuitionCalcResult]);

  useEffect(() => {
    if (stepIndex !== 1) setCalcError(null);
    if (
      stepIndex !== 1 &&
      stepIndex !== 2 &&
      stepIndex !== 3 &&
      stepIndex !== SUMMARY_STEP_INDEX
    ) {
      setOutOfBandModal(null);
    }
  }, [stepIndex]);

  const hasBandRangeOverlap = useMemo(
    () => stepIndex === 1 && hasAnyBandRangeOverlap(sizeBandRows),
    [stepIndex, sizeBandRows]
  );

  const openSizeOutOfBandModal = () => {
    setOutOfBandNameSearch('');
    setOutOfBandNameSearchDebounced('');
    setOutOfBandStudentsSort('desc');
    setOutOfBandTablePage(1);
    setOutOfBandModal({ type: 'size' });
  };

  /** Debounced name search + reset to page 1 when the query changes (not on pagination). */
  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = outOfBandNameSearch.trim();
      setOutOfBandNameSearchDebounced((prev) => {
        if (prev !== next) setOutOfBandTablePage(1);
        return next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [outOfBandNameSearch]);

  /** Server-paged school-by-ids: skip, take, name filter, sorting. */
  useEffect(() => {
    if (!outOfBandModal) return;

    if (modalSchoolIds.length === 0) {
      setOutOfBandItems([]);
      setOutOfBandTotalCount(0);
      setOutOfBandLoading(false);
      return;
    }

    const y = Number(workflow?.year);
    const min = workflow?.minSchoolsPerBand;
    if (!Number.isFinite(y) || min == null || min <= 0) return;

    let cancelled = false;
    setOutOfBandLoading(true);
    setOutOfBandError(null);

    (async () => {
      try {
        const result = await schoolBandsApi.getSchoolsByIds({
          year: y,
          minSchoolsPerBand: min,
          schoolIds: modalSchoolIds,
          name: outOfBandNameSearchDebounced || undefined,
          skipCount: (outOfBandTablePage - 1) * OUT_OF_BAND_PAGE_SIZE,
          maxResultCount: OUT_OF_BAND_PAGE_SIZE,
          sorting: 'TotalStudentCount',
          dir: outOfBandStudentsSort,
        });
        if (cancelled) return;
        setOutOfBandItems(result.items);
        setOutOfBandTotalCount(result.totalCount);
      } catch (e) {
        if (cancelled) return;
        setOutOfBandError(
          e instanceof ApiError
            ? ApiError.getUserMessage(e) ?? 'Could not load schools.'
            : 'Could not load schools.'
        );
        setOutOfBandItems([]);
        setOutOfBandTotalCount(0);
      } finally {
        if (!cancelled) setOutOfBandLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    outOfBandModal,
    modalSchoolIds,
    workflow?.year,
    workflow?.minSchoolsPerBand,
    outOfBandTablePage,
    outOfBandNameSearchDebounced,
    outOfBandStudentsSort,
  ]);

  const toggleOutOfBandStudentsSort = () => {
    setOutOfBandStudentsSort((s) => (s === 'asc' ? 'desc' : 'asc'));
    setOutOfBandTablePage(1);
  };

  /** Restore size bands from GET /school-bands (same as first load of step 2), then calc runs. */
  const resetSizeBandsToServerDefaults = useCallback(async () => {
    if (workflow?.minSchoolsPerBand == null || workflow.minSchoolsPerBand <= 0) return;
    setCalcError(null);
    setSizeBandsServerResetPending(true);
    try {
      const { data } = await sizeBandsQuery.refetch();
      const items = data?.items ?? [];
      if (items.length > 0) {
        setSizeBandRows(mapSchoolBandsToEditableRows(items));
      } else {
        setSizeBandRows([
          {
            key: `new-${++newBandKeyRef.current}`,
            name: '',
            minValue: 0,
            maxValue: 0,
            description: '',
            numberOfSchools: 0,
            schoolIds: {},
          },
        ]);
      }
    } finally {
      setSizeBandsServerResetPending(false);
    }
  }, [workflow?.minSchoolsPerBand, sizeBandsQuery]);

  const updateTuitionBandAt = useCallback(
    (
      schoolBandIndex: number,
      tuitionIndex: number,
      patch: Partial<NestedTuitionBandDto>
    ) => {
      setTuitionCalcResult((prev) => {
        if (!prev?.[schoolBandIndex]) return prev;
        const next = [...prev];
        const sb = { ...next[schoolBandIndex] };
        const tb = [...(sb.tuitionBands ?? [])];
        const row = tb[tuitionIndex];
        if (!row) return prev;
        tb[tuitionIndex] = { ...row, ...patch };
        sb.tuitionBands = tb;
        next[schoolBandIndex] = sb;
        return next;
      });
    },
    []
  );

  const addTuitionBand = useCallback(
    (schoolBandIndex: number) => {
      const year = Number(workflow?.year) || 0;
      const min = workflow?.minSchoolsPerBand ?? 0;
      setTuitionCalcResult((prev) => {
        if (!prev?.[schoolBandIndex]) return prev;
        const next = [...prev];
        const sb = { ...next[schoolBandIndex] };
        const tb = [...(sb.tuitionBands ?? [])];
        const parentId = sb.id;
        tb.push({
          id: newNestedTuitionBandId(),
          name: 'Tuition band',
          minValue: 0,
          maxValue: 0,
          minSchoolsPerBand: min,
          year,
          numberOfSchools: 0,
          schoolIds: [],
          schoolBandId:
            parentId && parentId.toLowerCase() !== CALC_BAND_NEW_ID
              ? parentId
              : undefined,
        });
        sb.tuitionBands = tb;
        next[schoolBandIndex] = sb;
        /** Re-sort just this school band so the new entry lands by amount (ascending). User typing edits go through `updateTuitionBandAt` which deliberately does not re-sort, to avoid rows jumping mid-edit. */
        return sortTuitionBandsByAmount(next);
      });
    },
    [workflow?.year, workflow?.minSchoolsPerBand]
  );

  const removeTuitionBand = useCallback(
    (schoolBandIndex: number, tuitionIndex: number) => {
      setTuitionCalcResult((prev) => {
        if (!prev?.[schoolBandIndex]) return prev;
        const next = [...prev];
        const sb = { ...next[schoolBandIndex] };
        const tb = [...(sb.tuitionBands ?? [])];
        tb.splice(tuitionIndex, 1);
        sb.tuitionBands = tb;
        next[schoolBandIndex] = sb;
        return next;
      });
    },
    []
  );

  const resetTuitionBandsToServerCalc = useCallback(async () => {
    const min = workflow?.minSchoolsPerBand;
    const year = Number(workflow?.year);
    if (min == null || min <= 0 || !Number.isFinite(year)) return;
    setTuitionCalcError(null);
    setTuitionServerResetPending(true);
    setRebuildPending(true);
    try {
      const rows = sizeBandRowsRef.current;
      const invalid = rows.some(
        (r) =>
          r.name.trim() === '' ||
          !Number.isFinite(r.minValue) ||
          !Number.isFinite(r.maxValue) ||
          r.minValue > r.maxValue
      );
      if (invalid) {
        setTuitionCalcError('Fix size bands on the previous step, then try again.');
        return;
      }
      const body: CalcSchoolBandTuitionInputItem[] = rows.map((r) =>
        sizeBandRowToTuitionCalcItem(r, min, year)
      );
      const result = await tuitionBandApi.calcSchoolBandTuitionBands(body);
      setTuitionCalcResult(sortTuitionBandsByAmount(result ?? []));
      await syncOutOfBandTotalsFromServer();
    } catch (e) {
      setTuitionCalcError(
        e instanceof ApiError
          ? ApiError.getUserMessage(e) ?? 'Could not reload tuition bands.'
          : 'Could not reload tuition bands.'
      );
    } finally {
      setRebuildPending(false);
      setTuitionServerResetPending(false);
    }
  }, [workflow?.minSchoolsPerBand, workflow?.year, syncOutOfBandTotalsFromServer]);

  const openTuitionOutsideModal = useCallback((schoolBandIndex: number) => {
    const rows = tuitionCalcResultRef.current;
    if (!rows?.[schoolBandIndex]) return;
    const sb = rows[schoolBandIndex];
    const ids = getSchoolIdsOutsideTuition(sb);
    const title = `Schools in "${sb.name ?? `Band ${schoolBandIndex + 1}`}" not in a tuition band`;
    setOutOfBandNameSearch('');
    setOutOfBandNameSearchDebounced('');
    setOutOfBandStudentsSort('desc');
    setOutOfBandTablePage(1);
    setOutOfBandModal({ type: 'tuition', title, schoolIds: ids });
  }, []);

  const minSchoolsValid = useMemo(
    () => parsePositiveMinSchools(minDraft) !== null,
    [minDraft]
  );

  const minSchoolsRequired = workflow?.minSchoolsPerBand ?? 0;

  const hasBandUnderMinSchools = useMemo(
    () =>
      stepIndex === 1 &&
      minSchoolsRequired > 0 &&
      sizeBandRows.some((r) => r.numberOfSchools < minSchoolsRequired),
    [stepIndex, sizeBandRows, minSchoolsRequired]
  );

  const step2CanProceed = useMemo(() => {
    if (stepIndex !== 1) return true;
    if (sizeBandsQuery.isPending || sizeBandsQuery.isFetching) return false;
    if (sizeBandsQuery.isError) return false;
    if (!sizeBandRows.length) return false;
    if (calcPending) return false;
    if (calcError) return false;
    if (hasBandUnderMinSchools) return false;
    if (totalSchoolOutOfBands !== 0) return false;
    if (!bandNamesAreUnique(sizeBandRows)) return false;
    return sizeBandRows.every(
      (r) =>
        r.name.trim() !== '' &&
        Number.isFinite(r.minValue) &&
        Number.isFinite(r.maxValue) &&
        r.minValue <= r.maxValue
    );
  }, [
    stepIndex,
    sizeBandsQuery.isPending,
    sizeBandsQuery.isFetching,
    sizeBandsQuery.isError,
    sizeBandRows,
    calcPending,
    calcError,
    hasBandUnderMinSchools,
    totalSchoolOutOfBands,
  ]);

  /** One line per school size band that has overlapping nested tuition USD ranges (only tiers under that band are compared). */
  const tuitionSubBandOverlaps = useMemo(() => {
    if (!tuitionCalcResult) return [] as string[];
    const lines: string[] = [];
    for (let sbIdx = 0; sbIdx < tuitionCalcResult.length; sbIdx++) {
      const sb = tuitionCalcResult[sbIdx];
      const tb = sb.tuitionBands ?? [];
      if (tb.length < 2) continue;
      const pairs = findOverlappingBandPairIndices(
        tb.map((x) => ({ minValue: x.minValue, maxValue: x.maxValue }))
      );
      if (pairs.length === 0) continue;
      const sizeName = sb.name?.trim() || `Size band ${sbIdx + 1}`;
      const pairLabels = pairs.map(([a, b]) => {
        const na = labelNestedTuitionBand(tb[a], a);
        const nb = labelNestedTuitionBand(tb[b], b);
        return `${na} and ${nb}`;
      });
      lines.push(
        `${sizeName}: overlapping tuition USD ranges between ${pairLabels.join(
          '; and between '
        )} (only among tuition tiers under this size band).`
      );
    }
    return lines;
  }, [tuitionCalcResult]);

  /** Size-band `schools` from tuition calc minus every nested tuition band’s `schools`. */
  const schoolsOutsideAllTuitionTiers = useMemo(
    () => collectSchoolsOutsideAllTuitionTiers(tuitionCalcResult),
    [tuitionCalcResult]
  );

  const tuitionStepOutsideSchoolsBanner = useMemo(() => {
    const hasTuitionData = Boolean(tuitionCalcResult?.length);
    if (hasTuitionData) {
      const count = schoolsOutsideAllTuitionTiers.length;
      return {
        hasTuitionData: true as const,
        count,
        clickable: count > 0,
        label: 'Schools not in any tuition band',
        description:
          count > 0
            ? 'Listed on a size band in the latest tuition calculation but not on any nested tuition tier’s school list. Click the count for name, code, total students, tuition (USD), and the size band (with student-count bounds).'
            : 'Every school on the size-band lists appears under at least one tuition tier.',
      };
    }
    return {
      hasTuitionData: false as const,
      count: totalSchoolOutOfBands,
      clickable: totalSchoolOutOfBands > 0,
      label: 'Schools outside all size bands',
      description:
        'Not in any size band (server count until tuition calculation runs). This refreshes with tuition rebuilds.',
    };
  }, [
    tuitionCalcResult?.length,
    schoolsOutsideAllTuitionTiers.length,
    totalSchoolOutOfBands,
  ]);

  const reviewHeatmapModel = useMemo(() => {
    if (stepIndex !== SUMMARY_STEP_INDEX) return null;
    return buildReviewHeatmap(tuitionCalcResult ?? null);
  }, [stepIndex, tuitionCalcResult]);

  const reviewSummaryModel = useMemo(() => {
    if (stepIndex !== SUMMARY_STEP_INDEX) return null;
    return buildReviewSummary(tuitionCalcResult ?? null);
  }, [stepIndex, tuitionCalcResult]);

  const reviewSizeBandChartData = useMemo(() => {
    if (stepIndex !== SUMMARY_STEP_INDEX || !tuitionCalcResult?.length) return [];
    return tuitionCalcResult.map((sb, i) => ({
      name: sb.name ?? `Band ${i + 1}`,
      schools:
        typeof sb.numberOfSchools === 'number' && sb.numberOfSchools >= 0
          ? sb.numberOfSchools
          : Object.keys(sb.schoolIds ?? {}).length,
    }));
  }, [stepIndex, tuitionCalcResult]);

  const reviewTuitionNameChartData = useMemo(() => {
    if (stepIndex !== SUMMARY_STEP_INDEX || !tuitionCalcResult?.length) return [];
    const acc = new Map<string, number>();
    for (const sb of tuitionCalcResult) {
      for (const tb of sb.tuitionBands ?? []) {
        const label =
          (tb.name && String(tb.name).trim()) ||
          `${formatTuitionAmount(tb.minValue)}–${formatTuitionAmount(tb.maxValue)}`;
        const n = tuitionSubBandSchoolCount(tb);
        acc.set(label, (acc.get(label) ?? 0) + n);
      }
    }
    return [...acc.entries()]
      .map(([name, schools]) => ({ name, schools }))
      .sort((a, b) => b.schools - a.schools)
      .slice(0, 16);
  }, [stepIndex, tuitionCalcResult]);

  const tuitionStepSchoolBandOverlap = useMemo(
    () => stepIndex === 2 && hasAnyBandRangeOverlap(sizeBandRows),
    [stepIndex, sizeBandRows]
  );

  const parentStepBandRangeOverlap = useMemo(
    () => stepIndex === 3 && hasAnyBandRangeOverlap(parentBandRows),
    [stepIndex, parentBandRows]
  );

  const parentStepBandNamesInvalid = useMemo(
    () =>
      stepIndex === 3 && parentBandRows.length > 0 && !bandNamesAreUnique(parentBandRows),
    [stepIndex, parentBandRows]
  );

  const parentStepBandBoundsInvalid = useMemo(
    () =>
      stepIndex === 3 &&
      parentBandRows.some(
        (r) =>
          !r.name.trim() ||
          !Number.isFinite(r.minValue) ||
          !Number.isFinite(r.maxValue) ||
          r.minValue > r.maxValue
      ),
    [stepIndex, parentBandRows]
  );

  const parentBandOverlapLines = useMemo(() => {
    if (!parentStepBandRangeOverlap) return [] as string[];
    return findOverlappingBandPairIndices(parentBandRows).map(([a, b]) => {
      const ra = parentBandRows[a];
      const rb = parentBandRows[b];
      const na = ra.name.trim() || `Band ${a + 1}`;
      const nb = rb.name.trim() || `Band ${b + 1}`;
      return `${na} (${formatTuitionAmount(ra.minValue)}–${formatTuitionAmount(
        ra.maxValue
      )}) and ${nb} (${formatTuitionAmount(rb.minValue)}–${formatTuitionAmount(
        rb.maxValue
      )}) overlap.`;
    });
  }, [parentStepBandRangeOverlap, parentBandRows]);

  const tuitionStepBandNamesInvalid = useMemo(
    () =>
      stepIndex === 2 &&
      sizeBandRows.length > 0 &&
      !bandNamesAreUnique(sizeBandRows),
    [stepIndex, sizeBandRows]
  );

  const step3CanProceed = useMemo(() => {
    if (stepIndex !== 2) return true;
    if (tuitionCalcPending || rebuildPending) return false;
    if (tuitionCalcError) return false;
    if (!tuitionCalcResult?.length) return false;
    return tuitionCalcResult.every((sb) =>
      (sb.tuitionBands ?? []).every(
        (tb) =>
          Number.isFinite(tb.minValue) &&
          Number.isFinite(tb.maxValue) &&
          tb.minValue <= tb.maxValue
      )
    );
  }, [stepIndex, tuitionCalcPending, rebuildPending, tuitionCalcError, tuitionCalcResult]);

  const step4CanProceed = useMemo(() => {
    if (stepIndex !== 3) return true;
    if (parentBandsQuery.isPending || parentBandsQuery.isFetching) return false;
    if (parentBandsCalcPending) return false;
    if (parentBandsQuery.isError) return false;
    if (parentBandsCalcError) return false;
    if (!parentBandRows.length) return false;
    if (!bandNamesAreUnique(parentBandRows)) return false;
    if (hasAnyBandRangeOverlap(parentBandRows)) return false;
    if (
      !parentBandRows.every(
        (r) =>
          r.name.trim() !== '' &&
          Number.isFinite(r.minValue) &&
          Number.isFinite(r.maxValue) &&
          r.minValue <= r.maxValue
      )
    ) {
      return false;
    }
    if (
      typeof parentMarketOutsideCount !== 'number' ||
      !Number.isFinite(parentMarketOutsideCount)
    ) {
      return false;
    }
    if (parentMarketOutsideCount > 0) return false;
    return true;
  }, [
    stepIndex,
    parentBandsQuery.isPending,
    parentBandsQuery.isFetching,
    parentBandsCalcPending,
    parentBandsQuery.isError,
    parentBandsCalcError,
    parentBandRows,
    parentMarketOutsideCount,
  ]);

  const canGoNext =
    stepIndex === 0
      ? minSchoolsValid
      : stepIndex === 1
        ? step2CanProceed
        : stepIndex === 2
          ? step3CanProceed
          : stepIndex === 3
            ? step4CanProceed
            : false;

  const canSubmitClassification = useMemo(() => {
    if (stepIndex !== SUMMARY_STEP_INDEX) return false;
    if (submitPending) return false;
    if (!tuitionCalcResult?.length) return false;
    if (tuitionCalcError) return false;
    if (rebuildPending || tuitionCalcPending) return false;
    if (parentBandsDirty && (parentBandsCalcPending || parentBandsCalcError)) return false;
    return tuitionCalcResult.every((sb) =>
      (sb.tuitionBands ?? []).every(
        (tb) =>
          Number.isFinite(tb.minValue) &&
          Number.isFinite(tb.maxValue) &&
          tb.minValue <= tb.maxValue
      )
    );
  }, [
    stepIndex,
    submitPending,
    tuitionCalcResult,
    tuitionCalcError,
    rebuildPending,
    tuitionCalcPending,
    parentBandsDirty,
    parentBandsCalcPending,
    parentBandsCalcError,
  ]);

  const handleMinChange = (v: string) => {
    setMinDraft(v);
    setShowStep1Error(false);
    const n = parsePositiveMinSchools(v);
    if (v.trim() === '') {
      patchWorkflow({ minSchoolsPerBand: null });
    } else if (n !== null) {
      patchWorkflow({ minSchoolsPerBand: n });
    }
  };

  const handleNext = () => {
    if (stepIndex === 0) {
      if (!minSchoolsValid) {
        setShowStep1Error(true);
        return;
      }
      setShowStep1Error(false);
    }
    if (stepIndex === 1) {
      if (!step2CanProceed) return;
    }
    if (stepIndex === 2) {
      if (!step3CanProceed) return;
    }
    if (stepIndex === 3) {
      if (!step4CanProceed) return;
    }
    if (stepIndex < STEP_LABELS.length - 1) {
      if (stepIndex === 1) {
        patchWorkflow({
          stepIndex: stepIndex + 1,
          sizeBandsDraft: JSON.stringify(sizeBandRows),
        });
      } else if (stepIndex === 2) {
        patchWorkflow({
          stepIndex: stepIndex + 1,
          tuitionBandsDraft: JSON.stringify(tuitionCalcResult ?? []),
        });
      } else {
        patchWorkflow({ stepIndex: stepIndex + 1 });
      }
    }
  };

  const updateSizeBandRow = (key: string, patch: Partial<SizeBandEditableRow>) => {
    setSizeBandRows((rows) =>
      rows.map((r) => (r.key === key ? { ...r, ...patch } : r))
    );
  };

  const addSizeBandRow = () => {
    setSizeBandRows((rows) => [
      ...rows,
      {
        key: `new-${++newBandKeyRef.current}`,
        name: '',
        minValue: 0,
        maxValue: 0,
        description: '',
        numberOfSchools: 0,
      },
    ]);
  };

  const removeSizeBandRow = (key: string) => {
    setSizeBandRows((rows) => {
      if (rows.length <= 1) return rows;
      return rows.filter((r) => r.key !== key);
    });
  };

  const handleBack = () => {
    if (stepIndex > 0) patchWorkflow({ stepIndex: stepIndex - 1 });
  };

  /** After submit (success or failure): leave wizard, clear year, reset local editors. */
  const returnToYearSelectionAfterSubmit = useCallback(() => {
    calcSeqRef.current += 1;
    tuitionCalcSeqRef.current += 1;
    parentCalcSeqRef.current += 1;
    tuitionRebuildSeqRef.current += 1;
    /** Drop cached band lists so step 2 / 4 cannot briefly show pre-submit data (invalidate alone may not refetch inactive queries). */
    void queryClient.removeQueries({ queryKey: schoolBandsQueryKeys.all });
    void queryClient.removeQueries({ queryKey: parentTuitionBandsQueryKeys.all });
    lastSizeBandsDataUpdatedAtRef.current = 0;
    setSelectedYear('');
    setYearLabel('');
    setShowStep1Error(false);
    setMinDraft('');
    setSizeBandRows([]);
    setTotalSchoolOutOfBands(0);
    setTotalSchoolOutOfBandsIds([]);
    setTuitionCalcResult(null);
    setTuitionCalcError(null);
    setParentBandRows([]);
    setParentBandsDirty(false);
    setParentBandsCalcPending(false);
    setParentBandsCalcError(null);
    setParentCalcTotals(null);
    setParentPopulateOutsideSchools(null);
    setParentSchoolModal(null);
    setParentSchoolModalPage(1);
    setParentMarketOutsideSearch('');
    setParentMarketOutsideSearchDebounced('');
    setOutOfBandModal(null);
    setOutOfBandLoading(false);
    setOutOfBandError(null);
    setOutOfBandItems([]);
    setOutOfBandTotalCount(0);
    setOutOfBandNameSearch('');
    setOutOfBandNameSearchDebounced('');
    setOutOfBandTablePage(1);
    setSizeBandSchoolsModal(null);
    setTuitionBandSchoolsModal(null);
    setCalcError(null);
    setCalcPending(false);
    setRebuildPending(false);
    setSizeBandsServerResetPending(false);
    setTuitionServerResetPending(false);
    setParentBandsServerResetPending(false);
  }, [queryClient, setSelectedYear]);

  const handleSubmitClassification = useCallback(async () => {
    const body = tuitionCalcResultRef.current;
    if (!body?.length || !workflow?.year) return;
    setSubmitPending(true);
    try {
      const yearNum = Number(workflow.year);
      const minSchools = workflow.minSchoolsPerBand ?? 0;
      const classificationPayload = buildCreateSchoolClassificationBasedOnBandsBody(
        body,
        parentBandRowsRef.current,
        minSchools,
        yearNum
      );
      await schoolClassificationApi.createBasedOnBands(classificationPayload);
      notification.success({
        message: 'Classification created',
        description:
          'Your size, tuition, and parent-facing tuition bands were saved. Reporting data was refreshed.',
        placement: 'bottomRight',
        duration: 6,
      });
    } catch (e) {
      const description =
        e instanceof ApiError
          ? (ApiError.getUserMessage(e) ?? 'Could not submit classification.')
          : e instanceof Error
            ? e.message
            : 'Could not submit classification.';
      notification.error({
        message: 'Classification could not be saved',
        description,
        placement: 'bottomRight',
        duration: 8,
      });
    } finally {
      setSubmitPending(false);
      returnToYearSelectionAfterSubmit();
    }
  }, [workflow?.year, workflow?.minSchoolsPerBand, returnToYearSelectionAfterSubmit]);

  const openParentBandSchools = useCallback(
    async (row: ParentBandEditableRow) => {
      const embedded = row.schools?.filter((s) => s.id?.trim()) ?? [];
      if (embedded.length > 0) {
        setParentSchoolModalPage(1);
        setParentSchoolModal({
          title: `${row.name?.trim() || 'Band'} — schools`,
          variant: 'bandMembersEmbedded',
          schools: embedded,
        });
        return;
      }
      const ids = Object.keys(row.schoolIds ?? {}).filter((id) => id.trim());
      if (ids.length > 0) {
        setParentSchoolModalPage(1);
        setParentSchoolModal({
          title: `${row.name?.trim() || 'Band'} — schools`,
          variant: 'bandMembers',
          schoolIds: ids,
        });
        return;
      }

      const n = row.numberOfSchools ?? 0;
      if (n <= 0) return;

      const min = workflow?.minSchoolsPerBand;
      const year = Number(workflow?.year);
      if (min == null || min <= 0 || !Number.isFinite(year)) return;

      const bandRows = parentBandsDirty
        ? parentBandRowsRef.current
        : mapParentTuitionBandsToEditableRows(parentBandsQuery.data?.items ?? []);
      if (!bandRows.length) return;

      const origIdx = bandRows.findIndex((r) => r.key === row.key);
      if (origIdx < 0) return;

      if (
        bandRows.some(
          (r) =>
            !Number.isFinite(r.minValue) ||
            !Number.isFinite(r.maxValue) ||
            r.minValue > r.maxValue
        )
      ) {
        message.warning('Fix band USD ranges before loading school lists.');
        return;
      }

      const closeLoading = message.loading('Loading schools…', 0);
      try {
        const order = sortBandRowIndicesByBounds(bandRows);
        const body: CalcBandSchoolCountItem[] = order.map((oi) =>
          parentRowToCalcBandPayload(bandRows[oi], min, year)
        );
        const result = await parentTuitionBandApi.populateBandMembership(year, body);
        const sortedPos = order.findIndex((oi) => oi === origIdx);
        const b = sortedPos >= 0 ? result.bands[sortedPos] : undefined;
        if (!b) {
          message.warning('No membership data returned for this band.');
          return;
        }

        const list = b.schools?.filter((s) => s.id?.trim()) ?? [];
        const titleBase = row.name?.trim() || b.name?.trim() || 'Band';
        setParentSchoolModalPage(1);
        if (list.length > 0) {
          setParentSchoolModal({
            title: `${titleBase} — schools`,
            variant: 'bandMembersEmbedded',
            schools: list,
          });
          return;
        }
        const sidKeys = Object.keys(b.schoolIds ?? {}).filter((id) => id.trim());
        if (sidKeys.length > 0) {
          setParentSchoolModal({
            title: `${titleBase} — schools`,
            variant: 'bandMembers',
            schoolIds: sidKeys,
          });
          return;
        }
        message.info('This band has no schools in the resolved membership.');
      } catch (e) {
        message.error(
          e instanceof ApiError
            ? ApiError.getUserMessage(e) ?? 'Could not load schools for this band.'
            : e instanceof Error
              ? e.message
              : 'Could not load schools for this band.'
        );
      } finally {
        closeLoading();
      }
    },
    [workflow?.minSchoolsPerBand, workflow?.year, parentBandsDirty, parentBandsQuery.data?.items]
  );

  const openSizeBandSchools = useCallback((row: SizeBandEditableRow) => {
    const embedded = row.schools?.filter((s) => s.id?.trim()) ?? [];
    if (embedded.length > 0) {
      setSizeBandSchoolsModal({
        title: `${row.name?.trim() || 'Size band'} — schools`,
        schools: embedded,
      });
      return;
    }
    const ids = Object.keys(row.schoolIds ?? {}).filter((id) => id.trim());
    if (ids.length === 0) return;
    setParentSchoolModal({
      title: `${row.name?.trim() || 'Size band'} — schools`,
      variant: 'bandMembers',
      schoolIds: ids,
    });
  }, []);

  const openTuitionBandEmbeddedSchools = useCallback(
    (sizeBandLabel: string, tuitionRow: NestedTuitionBandDto, tuitionIndex: number) => {
      const list = tuitionRow.schools?.filter((s) => s.id?.trim()) ?? [];
      if (list.length === 0) return;
      const tierName =
        tuitionRow.name?.trim() || `Tuition band ${tuitionIndex + 1}`;
      setTuitionBandSchoolsModal({
        title: `${sizeBandLabel} — ${tierName} — schools`,
        schools: list,
      });
    },
    []
  );

  const openSchoolsOutsideAllTuitionTiersModal = useCallback(() => {
    const list = collectSchoolsOutsideAllTuitionTiers(tuitionCalcResultRef.current);
    if (list.length === 0) return;
    setTuitionBandSchoolsModal({
      title: 'Schools not in any tuition band',
      schools: list,
      variant: 'outsideAllTuitionTiers',
    });
  }, []);

  const openParentMarketOutsideSchools = useCallback(() => {
    setParentMarketOutsideSearch('');
    setParentMarketOutsideSearchDebounced('');
    setParentMarketOutsideStudentsSort('desc');
    setParentSchoolModalPage(1);
    setParentSchoolModal({
      title: 'Schools outside parent-facing tuition bands',
      variant: 'parentMarketOutside',
    });
  }, []);

  const toggleParentMarketOutsideStudentsSort = useCallback(() => {
    setParentMarketOutsideStudentsSort((s) => (s === 'asc' ? 'desc' : 'asc'));
    setParentSchoolModalPage(1);
  }, []);

  const resetParentBandsFromServer = useCallback(async () => {
    const min = workflow?.minSchoolsPerBand;
    const year = Number(workflow?.year);
    if (min == null || min <= 0 || !Number.isFinite(year)) return;
    setParentBandsServerResetPending(true);
    try {
      const { data } = await parentBandsQuery.refetch();
      const items = data?.items ?? [];
      setParentBandRows(
        items.length > 0 ? mapParentTuitionBandsToEditableRows(items) : []
      );
      setParentBandsDirty(false);
      setParentBandsCalcError(null);
      await queryClient.invalidateQueries({ queryKey: parentTuitionBandsQueryKeys.all });
    } finally {
      setParentBandsServerResetPending(false);
    }
  }, [workflow?.minSchoolsPerBand, workflow?.year, parentBandsQuery, queryClient]);

  const addParentBandRow = useCallback(() => {
    const min = workflow?.minSchoolsPerBand;
    const year = Number(workflow?.year);
    if (min == null || min <= 0 || !Number.isFinite(year)) return;
    parentBandNewKeyRef.current += 1;
    setParentBandsDirty(true);
    setParentBandRows((prev) => [
      ...prev,
      {
        key: `new-parent-${parentBandNewKeyRef.current}`,
        name: nextUniqueParentBandName(prev),
        minValue: 0,
        maxValue: 0,
        description: '',
        numberOfSchools: 0,
        schoolIds: {},
        minSchoolsPerBand: min,
        year,
      },
    ]);
  }, [workflow?.minSchoolsPerBand, workflow?.year]);

  const removeParentBandRow = useCallback((key: string) => {
    setParentBandsDirty(true);
    setParentBandRows((prev) => prev.filter((r) => r.key !== key));
  }, []);

  const loadingBands =
    !!selectedYear &&
    (bandsQuery.isPending ||
      (hasBandsFromApi &&
        !workflow &&
        !showNewWorkflowWizard &&
        bandsQuery.isSuccess));

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <PageHeader
          title="Manage Classification"
          description="Configure all steps in order (core rules, size bands, tuition bands, then parent-facing tuition band). The final step gives a full overview with charts and KPIs; one submit sends all bands to the server to create or update the school classification and refreshes reporting data."
        />
      </div>

      <div className="flex flex-col gap-3 rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex shrink-0 items-center gap-2 text-slate-600">
            <CalendarIcon className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">Academic year</span>
          </div>
          <SearchableSelect
            value={selectedYear}
            onSelect={(id) => setSelectedYear(id)}
            fetchOptions={fetchYears}
            placeholder="Select year"
            selectedLabel={yearLabel || selectedYear}
            clearable
            className="min-w-[12rem]"
          />
          {selectedYear &&
            (loadingBands ? (
              <span className="text-xs text-slate-500">Loading bands…</span>
            ) : hasBandsFromApi ? (
              <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-emerald-600/20">
                Bands on server
              </span>
            ) : showNewWorkflowWizard ? (
              <span className="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-900 ring-1 ring-sky-600/20">
                New workflow (draft)
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900 ring-1 ring-amber-600/20">
                No bands for this year
              </span>
            ))}
        </div>
      </div>

      {!selectedYear && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/80 p-8 text-center text-sm text-slate-600">
          Select an academic year above to view or create a classification workflow.
        </div>
      )}

      {selectedYear && loadingBands && (
        <div className="rounded-xl border border-slate-200/80 bg-white p-10 text-center text-sm text-slate-600">
          Loading school bands for {selectedYear}…
        </div>
      )}

      {selectedYear &&
        !loadingBands &&
        !workflow &&
        !bandsQuery.isError && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/80 p-10 text-center">
            <p className="text-base font-medium text-slate-800">
              No classification workflow for <strong>{selectedYear}</strong>
            </p>
            <p className="mt-2 text-sm text-slate-600">
              Create a workflow to define school benchmarking bands (minimum schools per band, size
              bands, tuition bands, and review). Parent-facing tuition bands are configured
              separately below once available.
            </p>
            <Button
              type="button"
              variant="primary"
              className="mt-6"
              onClick={startNewWorkflow}
            >
              Create workflow for {selectedYear}
            </Button>
          </div>
        )}

      {selectedYear && bandsQuery.isError && !loadingBands && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/80 p-6 text-sm text-rose-900">
          Could not load school bands for this year. Please try again.
        </div>
      )}

      {selectedYear && !loadingBands && workflow && (
        <div className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm">
          <p className="mb-6 text-sm text-slate-600">
            Managing bands for:{' '}
            <span className="font-semibold text-slate-900">{workflow.year}</span>
            {hasBandsFromApi && (
              <span className="ml-2 text-emerald-700">· Editing existing bands from server</span>
            )}
            {showNewWorkflowWizard && !hasBandsFromApi && (
              <span className="ml-2 text-sky-800">· New workflow (not saved to server yet)</span>
            )}
            {workflow.updatedAt && (
              <span className="ml-2 text-slate-400">
                · Last updated {new Date(workflow.updatedAt).toLocaleString()}
              </span>
            )}
          </p>

          <div className="mb-6 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3">
            <h2 className="text-sm font-semibold text-indigo-950">
              Classification workflow
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-indigo-950/85">
              Steps 1–3 define <span className="font-semibold">school benchmarking</span> (size ×
              tuition). Step 4 holds <span className="font-semibold">parent-facing tuition band</span>{' '}
              settings (separate methodology). Step 5 is the only place you submit — after checking
              distributions for both systems.
            </p>
          </div>

          <ol className="mb-2 flex flex-wrap gap-2 sm:gap-4" aria-label="Classification workflow steps">
            {STEP_LABELS.map((label, i) => {
              const done = i < stepIndex;
              const active = i === stepIndex;
              return (
                <li key={label} className="flex min-w-0 flex-1 items-center gap-2">
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                      active
                        ? 'bg-indigo-600 text-white'
                        : done
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-slate-100 text-slate-500'
                    }`}
                    aria-current={active ? 'step' : undefined}
                  >
                    {done ? '✓' : i + 1}
                  </span>
                  <span
                    className={`hidden text-sm font-medium sm:inline ${
                      active ? 'text-indigo-900' : 'text-slate-600'
                    }`}
                  >
                    {label}
                  </span>
                </li>
              );
            })}
          </ol>
          <p className="mb-8 text-xs text-slate-500">
            Use <span className="font-medium text-slate-700">Next</span> to move through each step.
            <span className="font-medium text-slate-700"> Submit classification</span> is only on the
            last step, after you review both systems.
          </p>

          <div className="mb-2 sm:hidden">
            <p className="text-sm font-semibold text-slate-900">{STEP_LABELS[stepIndex]}</p>
          </div>

          <div className="min-h-[200px] rounded-lg border border-slate-100 bg-slate-50/50 p-5">
            {stepIndex === 0 && (
              <div className="space-y-5">
                <div
                  className="rounded-lg border border-indigo-100 bg-indigo-50/80 px-4 py-3 text-sm text-indigo-950"
                  role="note"
                >
                  <p className="font-medium text-indigo-900">Why this matters</p>
                  <p className="mt-1.5 leading-relaxed text-indigo-900/90">
                    Setting a minimum number of schools per band helps reduce the total number of
                    bands for {workflow.year}. That keeps the classification clearer and supports
                    reliable band suggestions.
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="minimum-schools-per-band"
                    className="block text-sm font-medium text-slate-800"
                  >
                    Minimum schools per band <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="minimum-schools-per-band"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    value={minDraft}
                    onChange={(e) => handleMinChange(e.target.value)}
                    onBlur={() => {
                      if (minDraft.trim() !== '' && !minSchoolsValid) {
                        setShowStep1Error(true);
                      }
                    }}
                    className="mt-2 w-full max-w-xs rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 shadow-sm outline-none ring-indigo-500/30 focus:border-indigo-500 focus:ring-2"
                    placeholder="e.g. 5"
                    aria-invalid={
                      showStep1Error || (minDraft !== '' && !minSchoolsValid)
                    }
                    aria-describedby="minimum-schools-hint minimum-schools-error"
                  />
                  <p id="minimum-schools-hint" className="mt-1.5 text-xs text-slate-500">
                    Whole number greater than zero. Applies only to {workflow.year}.
                  </p>
                  {(showStep1Error || (minDraft !== '' && !minSchoolsValid)) && (
                    <p
                      id="minimum-schools-error"
                      className="mt-2 text-sm text-red-600"
                      role="alert"
                    >
                      Please enter a valid whole number greater than 0.
                    </p>
                  )}
                </div>
              </div>
            )}

            {stepIndex === 1 && (
              <div className="space-y-5">
                <div className="rounded-2xl border border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-5 shadow-sm ring-1 ring-slate-100">
                  <p className="text-base font-semibold text-slate-900">
                    Size bands for {workflow.year}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Each band is a student enrollment range. Minimum schools per band for this
                    workflow:{' '}
                    <span className="font-bold tabular-nums text-indigo-900">
                      {workflow.minSchoolsPerBand ?? '—'}
                    </span>
                    . Names must be unique; min ≤ max. School counts and &quot;schools outside
                    bands&quot; refresh shortly after you edit boundaries.
                  </p>
                </div>

                <ServerResetCallout
                  title="Restore size bands from the server"
                  description="Replace the table below with the band definitions returned by the API for this year and minimum-schools setting. Your unsaved edits on this step are discarded."
                  actionLabel="Load server defaults"
                  onAction={() => void resetSizeBandsToServerDefaults()}
                  disabled={
                    !sizeBandsQuery.isSuccess ||
                    sizeBandsQuery.isFetching ||
                    calcPending ||
                    sizeBandsServerResetPending
                  }
                  pending={sizeBandsServerResetPending}
                />

                {sizeBandRows.length > 0 && (
                  <SizeBandsEnrollmentSpectrum rows={sizeBandRows} />
                )}

                {(sizeBandsQuery.isPending || sizeBandsQuery.isFetching) && (
                  <p className="text-sm text-slate-500">Loading size bands…</p>
                )}

                {calcPending && (
                  <p className="text-sm text-indigo-600">Updating school counts…</p>
                )}

                {calcError && (
                  <p className="text-sm text-red-600" role="alert">
                    {calcError}
                  </p>
                )}

                {hasBandRangeOverlap && (
                  <Alert
                    type="warning"
                    showIcon
                    className="mb-4"
                    message="Overlapping band ranges"
                    description={
                      <span>
                        Some min–max ranges overlap. When a school matches more than one band, it
                        is assigned to the first matching band when bands are ordered by minimum value
                        ascending. You can continue when every school is in a band, band names are
                        unique, and there are no schools outside bands.
                      </span>
                    }
                  />
                )}

                {sizeBandRows.length > 0 && !bandNamesAreUnique(sizeBandRows) && (
                  <Alert
                    type="warning"
                    showIcon
                    className="mb-4"
                    message="Band names must be unique"
                    description="Rename bands so each name is different (case-insensitive) before continuing."
                  />
                )}

                {sizeBandsQuery.isSuccess && sizeBandRows.length > 0 && (
                  <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm ring-1 ring-slate-100">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-semibold text-slate-800">
                        Schools outside bands
                      </span>
                      {totalSchoolOutOfBands > 0 ? (
                        <button
                          type="button"
                          onClick={openSizeOutOfBandModal}
                          className="inline-flex min-h-[1.75rem] items-center rounded-md bg-rose-600 px-3 py-0.5 text-xs font-semibold tabular-nums text-white shadow-sm ring-1 ring-rose-700/30 hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
                        >
                          {totalSchoolOutOfBands.toLocaleString()}
                        </button>
                      ) : (
                        <span className="inline-flex min-h-[1.75rem] items-center rounded-md bg-emerald-50 px-3 py-0.5 text-xs font-semibold tabular-nums text-emerald-900 ring-1 ring-emerald-200">
                          {totalSchoolOutOfBands.toLocaleString()}
                        </span>
                      )}
                      <span className="text-slate-600">
                        {totalSchoolOutOfBands > 0
                          ? 'Resolve band boundaries so all schools fall inside a band before continuing.'
                          : 'All schools are assigned to a band.'}
                      </span>
                    </div>
                  </div>
                )}

                {sizeBandsQuery.isError && (
                  <p className="text-sm text-red-600" role="alert">
                    Could not load size bands. Check your connection and try again.
                  </p>
                )}

                {sizeBandsQuery.isSuccess &&
                  (sizeBandsQuery.data?.totalCount ?? 0) === 0 &&
                  !sizeBandsQuery.isFetching &&
                  sizeBandRows.length === 0 && (
                    <p className="text-sm text-amber-800">
                      No bands returned for this year and minimum. Go back and adjust minimum schools
                      per band, or try again later.
                    </p>
                  )}

                {sizeBandsQuery.isSuccess && sizeBandRows.length > 0 && (
                  <div className="overflow-x-auto rounded-2xl border border-slate-200/90 bg-white shadow-md ring-1 ring-slate-100">
                    <table className="min-w-[800px] w-full border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                          <th className="px-3 py-2.5">Band name</th>
                          <th className="px-3 py-2.5">Min</th>
                          <th className="px-3 py-2.5">Max</th>
                          <th className="px-3 py-2.5">Description</th>
                          <th className="px-3 py-2.5 whitespace-nowrap">Schools</th>
                          <th className="px-3 py-2.5 w-24 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sizeBandRows.map((row) => {
                          const minReq = workflow.minSchoolsPerBand ?? 0;
                          const underMin =
                            minReq > 0 && row.numberOfSchools < minReq;
                          const sizeIdKeys = Object.keys(row.schoolIds ?? {});
                          const embeddedCount = row.schools?.length ?? 0;
                          const canOpenSizeSchools =
                            row.numberOfSchools > 0 &&
                            (embeddedCount > 0 || sizeIdKeys.length > 0);
                          return (
                          <tr
                            key={row.key}
                            className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80"
                          >
                            <td className="px-3 py-2 align-top">
                              <input
                                type="text"
                                value={row.name}
                                onChange={(e) =>
                                  updateSizeBandRow(row.key, { name: e.target.value })
                                }
                                className="w-full min-w-[8rem] rounded-md border border-slate-200 px-2 py-1.5 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <input
                                type="number"
                                inputMode="numeric"
                                value={row.minValue}
                                onChange={(e) =>
                                  updateSizeBandRow(row.key, {
                                    minValue: Number(e.target.value),
                                  })
                                }
                                className="w-24 rounded-md border border-slate-200 px-2 py-1.5 tabular-nums text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <input
                                type="number"
                                inputMode="numeric"
                                value={row.maxValue}
                                onChange={(e) =>
                                  updateSizeBandRow(row.key, {
                                    maxValue: Number(e.target.value),
                                  })
                                }
                                className="w-24 rounded-md border border-slate-200 px-2 py-1.5 tabular-nums text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              />
                            </td>
                            <td className="px-3 py-2 align-top">
                              <input
                                type="text"
                                value={row.description}
                                onChange={(e) =>
                                  updateSizeBandRow(row.key, {
                                    description: e.target.value,
                                  })
                                }
                                className="w-full min-w-[10rem] rounded-md border border-slate-200 px-2 py-1.5 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                placeholder="Optional"
                              />
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <span
                                className={`inline-flex flex-col gap-0.5 rounded-full px-3 py-1 text-xs font-semibold tabular-nums ring-1 ${
                                  underMin
                                    ? 'bg-rose-50 text-rose-800 ring-rose-200'
                                    : 'bg-indigo-50 text-indigo-900 ring-indigo-100'
                                }`}
                              >
                                {canOpenSizeSchools ? (
                                  <button
                                    type="button"
                                    onClick={() => openSizeBandSchools(row)}
                                    className="text-left font-semibold text-indigo-800 underline decoration-indigo-300 underline-offset-2 hover:text-indigo-950"
                                  >
                                    {row.numberOfSchools.toLocaleString()} schools
                                  </button>
                                ) : (
                                  <span
                                    title={
                                      row.numberOfSchools > 0 &&
                                      embeddedCount === 0 &&
                                      sizeIdKeys.length === 0
                                        ? 'School list not returned for this band yet — run recalculate or confirm the API includes schools or school IDs.'
                                        : undefined
                                    }
                                  >
                                    {row.numberOfSchools.toLocaleString()} schools
                                  </span>
                                )}
                                {underMin && (
                                  <span className="text-[10px] font-normal normal-case text-rose-700">
                                    Below minimum ({minReq})
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className="px-3 py-2 align-middle text-right">
                              <button
                                type="button"
                                onClick={() => removeSizeBandRow(row.key)}
                                disabled={sizeBandRows.length <= 1}
                                className="rounded-md px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
                                title={
                                  sizeBandRows.length <= 1
                                    ? 'At least one band is required'
                                    : 'Remove band'
                                }
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {sizeBandsQuery.isSuccess && sizeBandRows.length > 0 && (
                  <div className="mt-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={addSizeBandRow}
                    >
                      Add band
                    </Button>
                  </div>
                )}
              </div>
            )}
            {stepIndex === 2 && (
              <div className="space-y-5">
                <div className="rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50/50 via-white to-amber-50/30 p-5 shadow-sm ring-1 ring-orange-100/80">
                  <p className="text-base font-semibold text-slate-900">
                    Tuition bands for {workflow.year}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Each size band below has its own tuition tiers (amount ranges). Counts and
                    &quot;outside tuition&quot; update automatically when you change boundaries or
                    add or remove bands—no separate recalculate button.
                  </p>
                </div>

                <ServerResetCallout
                  title="Restore tuition layout from the server"
                  description="Reloads tuition sub-bands from the server using your current size bands from the previous step. Local edits on this step are replaced."
                  actionLabel="Load server tuition bands"
                  onAction={() => void resetTuitionBandsToServerCalc()}
                  disabled={
                    tuitionCalcPending ||
                    rebuildPending ||
                    tuitionServerResetPending ||
                    !tuitionCalcResult?.length
                  }
                  pending={tuitionServerResetPending}
                />

                {tuitionStepSchoolBandOverlap && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Overlapping size band ranges"
                    description="Some min–max ranges for your size bands overlap. Go back to the previous step to align boundaries if you want a single clear assignment per school."
                  />
                )}

                {tuitionStepBandNamesInvalid && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Band names must be unique"
                    description="Go back to Size bands and rename bands so each name is different before continuing."
                  />
                )}

                {tuitionCalcError && (
                  <Alert type="error" showIcon message={tuitionCalcError} />
                )}

                {tuitionSubBandOverlaps.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Overlapping tuition ranges within a size band"
                    description={
                      <div className="space-y-2">
                        <p className="text-sm">
                          Each line is only about tuition tiers under{' '}
                          <span className="font-medium">one</span> school size band. Low vs Middle
                          (for example) is never compared across Small vs Medium — only within the
                          tuition list for that same size category.
                        </p>
                        <ul className="list-disc space-y-1.5 pl-5 text-sm">
                          {tuitionSubBandOverlaps.map((line, idx) => (
                            <li key={`tuition-ov-${idx}`}>{line}</li>
                          ))}
                        </ul>
                        <p className="text-sm text-slate-600">
                          You can still continue if these ranges are acceptable for your process.
                        </p>
                      </div>
                    }
                  />
                )}

                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm ring-1 ring-slate-100">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-semibold text-slate-800">
                      {tuitionStepOutsideSchoolsBanner.label}
                    </span>
                    {tuitionStepOutsideSchoolsBanner.clickable ? (
                      <button
                        type="button"
                        onClick={
                          tuitionStepOutsideSchoolsBanner.hasTuitionData
                            ? openSchoolsOutsideAllTuitionTiersModal
                            : openSizeOutOfBandModal
                        }
                        className="inline-flex min-h-[1.75rem] items-center rounded-md bg-rose-600 px-3 py-0.5 text-xs font-semibold tabular-nums text-white shadow-sm ring-1 ring-rose-700/30 hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
                      >
                        {tuitionStepOutsideSchoolsBanner.count.toLocaleString()}
                      </button>
                    ) : (
                      <span className="inline-flex min-h-[1.75rem] items-center rounded-md bg-emerald-50 px-3 py-0.5 text-xs font-semibold tabular-nums text-emerald-900 ring-1 ring-emerald-200">
                        {tuitionStepOutsideSchoolsBanner.count.toLocaleString()}
                      </span>
                    )}
                    <span className="text-slate-600">
                      {tuitionStepOutsideSchoolsBanner.description}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Expand a size band to edit tuition tiers. Changes trigger an automatic school
                  assignment refresh. Use{' '}
                  <span className="font-medium text-indigo-800">Load server tuition bands</span>{' '}
                  above only when you want to discard local tuition edits.
                </p>

                <Spin spinning={tuitionCalcPending || rebuildPending}>
                  {!tuitionCalcPending &&
                    !rebuildPending &&
                    !tuitionCalcError &&
                    !tuitionCalcResult?.length && (
                      <Alert
                        type="info"
                        showIcon
                        message="Nothing to show yet"
                        description="Ensure size bands on the previous step have a name and min ≤ max. If ranges are invalid, go back and fix them."
                      />
                    )}

                  {!!tuitionCalcResult?.length && (
                    <Collapse
                      bordered
                      className="bg-white"
                      defaultActiveKey={[]}
                      items={tuitionCalcResult.map((sb, i) => {
                        const tb = sb.tuitionBands ?? [];
                        const schoolsFromSubBands =
                          tb.length > 0
                            ? totalSchoolsAcrossTuitionBands(tb)
                            : (sb.numberOfSchools ?? 0);
                        const outsideTuition = countSchoolsOutsideTuition(sb);
                        return {
                          key: String(i),
                          label: (
                            <div className="flex flex-wrap items-center justify-between gap-2 pr-2">
                              <span className="font-semibold text-slate-900">
                                {sb.name ?? `Band ${i + 1}`}
                              </span>
                              <div className="flex max-w-full flex-wrap items-center justify-end gap-x-3 gap-y-1 text-xs text-slate-500 tabular-nums">
                                <span>
                                  Students {sb.minValue.toLocaleString()}–
                                  {sb.maxValue.toLocaleString()}
                                </span>
                                <span className="font-medium text-slate-700">
                                  {schoolsFromSubBands.toLocaleString()} in tuition
                                  bands
                                </span>
                                {outsideTuition > 0 ? (
                                  <button
                                    type="button"
                                    className="rounded-md bg-amber-50 px-2 py-0.5 font-semibold text-amber-900 ring-1 ring-amber-200 hover:bg-amber-100"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openTuitionOutsideModal(i);
                                    }}
                                  >
                                    {outsideTuition.toLocaleString()} outside tuition
                                  </button>
                                ) : (
                                  <span className="text-slate-400">
                                    0 outside tuition
                                  </span>
                                )}
                              </div>
                            </div>
                          ),
                          children: (
                            <div className="space-y-3 pb-1">
                              {tb.length === 0 ? (
                                <p className="text-sm text-slate-500">
                                  {sb.numberOfSchools
                                    ? 'No tuition bands yet. Add one below, then recalculate.'
                                    : 'No schools in this size band.'}
                                </p>
                              ) : (
                                <div className="space-y-4">
                                  <TuitionBandsSpectrum bands={tb} />
                                  <ul className="grid gap-4 sm:grid-cols-2">
                                    {tb.map((row, j) => {
                                      const minOk =
                                        Number.isFinite(row.minValue) &&
                                        Number.isFinite(row.maxValue) &&
                                        row.minValue <= row.maxValue;
                                      const schoolCount =
                                        tuitionSubBandSchoolCount(row);
                                      const studentCount =
                                        typeof row.numberOfStudents === 'number' &&
                                        Number.isFinite(row.numberOfStudents)
                                          ? row.numberOfStudents
                                          : null;
                                      const tuitionEmbedded =
                                        row.schools?.filter((s) => s.id?.trim()) ??
                                        [];
                                      const canOpenTuitionSchools =
                                        schoolCount > 0 && tuitionEmbedded.length > 0;
                                      const sizeBandLabel =
                                        sb.name?.trim() || `Band ${i + 1}`;
                                      const accent =
                                        TUITION_SPECTRUM_PALETTE[
                                          j % TUITION_SPECTRUM_PALETTE.length
                                        ];
                                      return (
                                        <li
                                          key={
                                            row.id
                                              ? `${row.id}-${j}`
                                              : `${i}-tb-${j}`
                                          }
                                          className={`overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-md ring-1 ring-slate-100 transition-shadow hover:shadow-lg`}
                                        >
                                          <div
                                            className={`h-1.5 w-full bg-gradient-to-r ${accent}`}
                                            aria-hidden
                                          />
                                          <div className="space-y-3 p-4">
                                            <div className="flex items-start justify-between gap-3">
                                              <label className="block min-w-0 flex-1">
                                                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                                  Tuition band name
                                                </span>
                                                <input
                                                  type="text"
                                                  value={row.name ?? ''}
                                                  onChange={(e) =>
                                                    updateTuitionBandAt(i, j, {
                                                      name: e.target.value,
                                                    })
                                                  }
                                                  className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm font-semibold text-slate-900 focus:border-orange-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-400/30"
                                                  placeholder="e.g. Standard tier"
                                                />
                                              </label>
                                              {canOpenTuitionSchools ? (
                                                <button
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    openTuitionBandEmbeddedSchools(
                                                      sizeBandLabel,
                                                      row,
                                                      j
                                                    );
                                                  }}
                                                  className={`shrink-0 rounded-full px-3 py-1.5 text-left text-xs font-bold tabular-nums ring-2 ring-offset-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60 ${
                                                    minOk
                                                      ? 'bg-orange-50 text-orange-950 ring-orange-200 hover:bg-orange-100'
                                                      : 'bg-rose-50 text-rose-900 ring-rose-200 hover:bg-rose-100'
                                                  }`}
                                                  title="View schools in this tuition band"
                                                >
                                                  {schoolCount.toLocaleString()}{' '}
                                                  <span className="font-semibold opacity-90 underline decoration-orange-300/80 underline-offset-2">
                                                    schools
                                                  </span>
                                                </button>
                                              ) : (
                                                <span
                                                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold tabular-nums ring-2 ring-offset-1 ${
                                                    minOk
                                                      ? 'bg-orange-50 text-orange-950 ring-orange-200'
                                                      : 'bg-rose-50 text-rose-900 ring-rose-200'
                                                  }`}
                                                  title="Schools in this band (last recalculate)"
                                                >
                                                  {schoolCount.toLocaleString()}{' '}
                                                  <span className="font-semibold opacity-90">
                                                    schools
                                                  </span>
                                                </span>
                                              )}
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                              <label className="block">
                                                <span className="text-[11px] font-medium text-slate-600">
                                                  Min amount
                                                </span>
                                                <input
                                                  type="number"
                                                  inputMode="decimal"
                                                  value={
                                                    Number.isFinite(row.minValue)
                                                      ? row.minValue
                                                      : ''
                                                  }
                                                  onChange={(e) =>
                                                    updateTuitionBandAt(i, j, {
                                                      minValue: Number(
                                                        e.target.value
                                                      ),
                                                    })
                                                  }
                                                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 tabular-nums text-slate-900 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/25"
                                                />
                                              </label>
                                              <label className="block">
                                                <span className="text-[11px] font-medium text-slate-600">
                                                  Max amount
                                                </span>
                                                <input
                                                  type="number"
                                                  inputMode="decimal"
                                                  value={
                                                    Number.isFinite(row.maxValue)
                                                      ? row.maxValue
                                                      : ''
                                                  }
                                                  onChange={(e) =>
                                                    updateTuitionBandAt(i, j, {
                                                      maxValue: Number(
                                                        e.target.value
                                                      ),
                                                    })
                                                  }
                                                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 tabular-nums text-slate-900 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/25"
                                                />
                                              </label>
                                            </div>
                                            <div className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs tabular-nums text-slate-600">
                                              <span>Students in this band</span>
                                              <span className="font-semibold text-slate-800">
                                                {studentCount == null
                                                  ? '—'
                                                  : studentCount.toLocaleString()}
                                              </span>
                                            </div>
                                            {!minOk && (
                                              <p className="text-xs font-medium text-rose-700">
                                                Min must be ≤ max before recalculate.
                                              </p>
                                            )}
                                            <div className="flex justify-end border-t border-slate-100 pt-3">
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  removeTuitionBand(i, j)
                                                }
                                                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                                              >
                                                Remove band
                                              </button>
                                            </div>
                                          </div>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              )}
                              <Button
                                type="button"
                                variant="secondary"
                                className="w-full sm:w-auto"
                                onClick={() => addTuitionBand(i)}
                              >
                                Add tuition band
                              </Button>
                            </div>
                          ),
                        };
                      })}
                    />
                  )}
                </Spin>
              </div>
            )}
            {stepIndex === 3 && (
              <div className="space-y-5">
                <div className="rounded-lg border border-violet-100 bg-violet-50/50 px-4 py-3 text-sm text-violet-950">
                  <p className="font-medium text-violet-900">B. Parent-facing tuition band category</p>
                  <p className="mt-1.5 leading-relaxed text-violet-950/90">
                    National tuition bands use enrollment-weighted quantiles so each band represents
                    a similar share of students. This is independent of the size and tuition bands
                    you configured in earlier steps.
                  </p>
                </div>
                <p className="text-sm text-slate-600">
                  Bands below are returned by the server for your year and minimum schools per band.
                  Names must be unique, USD ranges must not overlap, and every school must fall inside
                  a band (none outside) before you can continue. Review them, then go to the summary
                  to see both systems side by side.
                </p>

                <ServerResetCallout
                  title="Restore parent-facing tuition bands from the server"
                  description="Replace the table below with the band definitions returned by the API for this year and minimum-schools setting. Unsaved edits on this step and any pending deletes are discarded."
                  actionLabel="Load server bands"
                  onAction={() => void resetParentBandsFromServer()}
                  disabled={
                    !parentBandsQuery.isSuccess ||
                    parentBandsQuery.isFetching ||
                    parentBandsServerResetPending
                  }
                  pending={parentBandsServerResetPending}
                />

                {parentStepBandRangeOverlap && (
                  <Alert
                    type="warning"
                    showIcon
                    className="mb-0"
                    message="Overlapping parent-facing tuition ranges"
                    description={
                      <div className="space-y-2">
                        <p className="text-sm">
                          USD min–max ranges must not overlap. Adjust boundaries so each school maps
                          to at most one band before continuing.
                        </p>
                        {parentBandOverlapLines.length > 0 && (
                          <ul className="list-disc space-y-1.5 pl-5 text-sm">
                            {parentBandOverlapLines.map((line, idx) => (
                              <li key={`parent-ov-${idx}`}>{line}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    }
                  />
                )}

                {parentStepBandNamesInvalid && (
                  <Alert
                    type="warning"
                    showIcon
                    className="mb-0"
                    message="Band names must be unique"
                    description="Rename parent-facing tuition bands so each name is different (case-insensitive) before continuing."
                  />
                )}

                {parentStepBandBoundsInvalid && (
                  <Alert
                    type="warning"
                    showIcon
                    className="mb-0"
                    message="Band fields incomplete or invalid"
                    description="Each band needs a non-empty name and min USD ≤ max USD (finite numbers)."
                  />
                )}

                {parentBandsQuery.isSuccess && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm ring-1 ring-slate-100">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Counts from GET /api/app/parent-tuition-band
                      {parentBandsDirty ? ' and live membership refresh while you edit' : ''}
                    </p>
                    <ul className="mt-2 space-y-2 text-sm text-slate-800">
                      <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span className="font-semibold text-slate-800">Total schools in market</span>
                        <span className="tabular-nums text-slate-900">
                          {typeof parentMarketTotalSchools === 'number' &&
                          Number.isFinite(parentMarketTotalSchools)
                            ? parentMarketTotalSchools.toLocaleString()
                            : parentBandsCalcPending
                              ? '…'
                              : '—'}
                        </span>
                      </li>
                      <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span className="font-semibold text-slate-800">
                          Schools in a band (Σ per-band numberOfSchools)
                        </span>
                        <span className="tabular-nums text-slate-900">
                          {parentStepSchoolsInBands.toLocaleString()}
                        </span>
                      </li>
                      <li className="flex flex-wrap items-center gap-x-2 gap-y-2">
                        <span className="font-semibold text-slate-800">Schools outside bands</span>
                        {(() => {
                          const n =
                            typeof parentMarketOutsideCount === 'number' &&
                            Number.isFinite(parentMarketOutsideCount)
                              ? parentMarketOutsideCount
                              : null;
                          if (n === null) {
                            return (
                              <span className="tabular-nums text-slate-500">
                                {parentBandsCalcPending ? '…' : '—'}
                              </span>
                            );
                          }
                          return n > 0 ? (
                            <button
                              type="button"
                              onClick={openParentMarketOutsideSchools}
                              className="inline-flex min-h-[1.75rem] items-center rounded-md bg-rose-600 px-3 py-0.5 text-xs font-semibold tabular-nums text-white shadow-sm ring-1 ring-rose-700/30 hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/50"
                            >
                              {n.toLocaleString()}
                            </button>
                          ) : (
                            <span className="inline-flex min-h-[1.75rem] items-center rounded-md bg-emerald-50 px-3 py-0.5 text-xs font-semibold tabular-nums text-emerald-900 ring-1 ring-emerald-200">
                              {n.toLocaleString()}
                            </span>
                          );
                        })()}
                        <span className="text-slate-600">
                          {typeof parentMarketOutsideCount === 'number' &&
                          parentMarketOutsideCount > 0
                            ? 'Not assigned to any parent-facing tuition band. Click the count to search, sort by students, and page through the list.'
                            : typeof parentMarketOutsideCount === 'number'
                              ? 'All loaded schools fall inside a parent-facing tuition band for this year.'
                              : parentBandsDirty
                                ? 'Refreshing band membership for your draft bands…'
                                : 'Load bands or adjust filters to see outside counts.'}
                        </span>
                      </li>
                    </ul>
                  </div>
                )}

                {typeof parentMarketOutsideCount === 'number' && parentMarketOutsideCount > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    className="mb-0"
                    message="Schools outside parent-facing tuition bands"
                    description={
                      <span>
                        {parentMarketOutsideCount.toLocaleString()} school
                        {parentMarketOutsideCount === 1 ? ' is' : 's are'} not assigned to any
                        parent-facing tuition band. Adjust band boundaries or save changes, then reload
                        until this count is zero before continuing. Use the schools-outside count
                        in the summary above to open the list.
                      </span>
                    }
                  />
                )}
                <ParentTuitionBandsTable
                  mode="edit"
                  rows={parentBandRows}
                  onRowsChange={(next) => {
                    setParentBandsDirty(true);
                    setParentBandRows(next);
                  }}
                  onAddRow={addParentBandRow}
                  onRemoveRow={removeParentBandRow}
                  onOpenSchools={openParentBandSchools}
                  totalCount={parentBandsQuery.data?.totalCount ?? 0}
                  isPending={parentBandsQuery.isPending}
                  isFetching={parentBandsQuery.isFetching || parentBandsCalcPending}
                  errorMessage={parentBandsCalcError ?? parentBandsErrorMsg}
                  yearLabel={workflow.year}
                  minSchools={workflow.minSchoolsPerBand ?? 0}
                  showChart={false}
                  showParentMarketCoverage={false}
                />
              </div>
            )}
            {stepIndex === SUMMARY_STEP_INDEX && (
              <div className="space-y-8">
                <div className="rounded-lg border border-slate-200 bg-slate-50/90 px-4 py-3 text-sm text-slate-700">
                  <p className="font-semibold text-slate-900">Final review</p>
                  <p className="mt-1">
                    Review the snapshot of your rules and how schools map under both lenses. Submit
                    sends size, tuition, and parent-facing tuition bands in one request so the server can
                    create or update them, then reporting data is refreshed.
                  </p>
                </div>
                {parentBandsDirty && (
                  <Alert
                    type="info"
                    showIcon
                    message="Parent-facing tuition bands not saved yet"
                    description="You have edits from step 4 that are not persisted yet. Submit classification below sends them with the rest of the bands so the server can create or update them in one step."
                  />
                )}

                <section
                  className="space-y-5 rounded-2xl border border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-indigo-50/40 p-5 shadow-sm ring-1 ring-slate-100"
                  aria-labelledby="summary-overview-heading"
                >
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <h2
                        id="summary-overview-heading"
                        className="text-lg font-semibold tracking-tight text-slate-900"
                      >
                        Configuration overview
                      </h2>
                      <p className="mt-1 text-sm text-slate-600">
                        Academic year{' '}
                        <span className="font-semibold tabular-nums text-slate-900">
                          {workflow.year}
                        </span>
                        {' · '}
                        Minimum{' '}
                        <span className="font-semibold tabular-nums text-slate-900">
                          {workflow.minSchoolsPerBand ?? '—'}
                        </span>{' '}
                        schools per band
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {STEP_LABELS.slice(0, 4).map((label, i) => (
                        <span
                          key={label}
                          className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50/90 px-2.5 py-1 text-xs font-medium text-emerald-900"
                        >
                          <span
                            className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white"
                            aria-hidden
                          >
                            {i + 1}
                          </span>
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                    <div className="rounded-xl border border-white/80 bg-white/90 p-4 shadow-sm ring-1 ring-slate-100">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Size bands
                      </p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                        {(reviewSummaryModel?.schoolBandCount ?? sizeBandRows.length).toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/80 bg-white/90 p-4 shadow-sm ring-1 ring-slate-100">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Tuition tiers
                      </p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                        {reviewSummaryModel
                          ? reviewSummaryModel.tuitionBandCount.toLocaleString()
                          : '—'}
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/80 bg-white/90 p-4 shadow-sm ring-1 ring-slate-100">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Parent-facing tuition bands
                      </p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-violet-950">
                        {summaryParentBandRows.length.toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/80 bg-white/90 p-4 shadow-sm ring-1 ring-slate-100">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Schools in size bands
                      </p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                        {(reviewSummaryModel?.schoolsInSizeBands ?? '—').toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/80 bg-white/90 p-4 shadow-sm ring-1 ring-slate-100">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Tuition coverage
                      </p>
                      <p className="mt-1 text-lg font-bold tabular-nums leading-snug text-slate-900">
                        <span className="text-emerald-800">
                          {(reviewSummaryModel?.schoolsAssignedToTuition ?? 0).toLocaleString()}
                        </span>
                        <span className="mx-1 font-normal text-slate-400">/</span>
                        <span className="text-amber-800">
                          {(reviewSummaryModel?.schoolsOutsideTuition ?? 0).toLocaleString()}
                        </span>
                      </p>
                      <p className="mt-0.5 text-[10px] text-slate-500">In tier / outside tier</p>
                    </div>
                    <div className="rounded-xl border border-white/80 bg-white/90 p-4 shadow-sm ring-1 ring-violet-100">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-800">
                        Parent-facing tuition band
                      </p>
                      <p className="mt-1 text-lg font-bold tabular-nums text-violet-950">
                        {typeof summaryParentListAggregates.totalNumberOfSchools === 'number'
                          ? summaryParentListAggregates.totalNumberOfSchools.toLocaleString()
                          : '—'}{' '}
                        <span className="text-sm font-normal text-violet-700">total</span>
                      </p>
                      <p className="mt-0.5 text-xs font-semibold text-amber-800">
                        {(typeof summaryParentListAggregates.totalNumberOfSchoolsOutsideBands ===
                        'number'
                          ? summaryParentListAggregates.totalNumberOfSchoolsOutsideBands
                          : 0
                        ).toLocaleString()}{' '}
                        outside bands
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    {summaryParentPieData.length > 0 && (
                      <div className="rounded-xl border border-violet-100 bg-white p-4 shadow-sm ring-1 ring-violet-100/70">
                        <h3 className="text-sm font-semibold text-violet-950">
                          Parent-facing tuition band school mix
                        </h3>
                        <p className="mt-0.5 text-xs text-slate-600">
                          Share of schools inside national parent-facing tuition bands vs outside
                          (same totals as section B).
                        </p>
                        <div className="mx-auto mt-2 h-[220px] w-full max-w-sm">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={summaryParentPieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={52}
                                outerRadius={80}
                                paddingAngle={1}
                              >
                                {summaryParentPieData.map((entry) => (
                                  <Cell key={`cell-${entry.name}`} fill={entry.fill} />
                                ))}
                              </Pie>
                              <Tooltip
                                formatter={(v: number) => v.toLocaleString()}
                                itemStyle={{ fontSize: 12 }}
                              />
                              <Legend wrapperStyle={{ fontSize: 12 }} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                    {reviewSizeBandChartData.length > 0 && (
                      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <h3 className="text-sm font-semibold text-slate-900">
                          Schools per size band (quick view)
                        </h3>
                        <p className="mt-0.5 text-xs text-slate-600">
                          Same data as the detailed chart in section A — useful for a last glance
                          before submit.
                        </p>
                        <div className="mt-2 h-[220px] w-full min-w-0">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={reviewSizeBandChartData}
                              margin={{ top: 4, right: 8, left: 4, bottom: 40 }}
                            >
                              <XAxis
                                dataKey="name"
                                tick={{ fontSize: 10 }}
                                interval={0}
                                angle={-22}
                                textAnchor="end"
                                height={48}
                              />
                              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={36} />
                              <Tooltip
                                formatter={(v: number) => [v.toLocaleString(), 'Schools']}
                                labelFormatter={(l) => String(l)}
                              />
                              <Bar
                                dataKey="schools"
                                fill="#4f46e5"
                                radius={[4, 4, 0, 0]}
                                maxBarSize={36}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                <section
                  className="space-y-6 rounded-xl border border-indigo-100 bg-white p-5 shadow-sm ring-1 ring-indigo-100/60"
                  aria-labelledby="summary-a-heading"
                >
                  <h3 id="summary-a-heading" className="text-base font-semibold text-indigo-950">
                    A. School benchmarking — distribution
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    The heatmap is <span className="font-medium">grouped by school size band</span>:
                    each section lists the tuition bands defined for that enrollment range, then the
                    grid shows how many schools fall in each tier. Striped cells are not applicable
                    for that row.
                  </p>

                {!tuitionCalcResult?.length && (
                  <Alert
                    type="info"
                    showIcon
                    message="No classification data"
                    description="Go back through the wizard so size and tuition bands are calculated, then return here."
                  />
                )}

                {tuitionCalcResult && tuitionCalcResult.length > 0 && !reviewHeatmapModel && (
                  <Alert
                    type="info"
                    showIcon
                    message="Nothing to plot yet"
                    description="Tuition bands with school counts are required for the heatmap. Complete the tuition step and run school assignment recalculation."
                  />
                )}

                {reviewSummaryModel && (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        School size bands
                      </p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                        {reviewSummaryModel.schoolBandCount.toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Tuition bands (nested)
                      </p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                        {reviewSummaryModel.tuitionBandCount.toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Schools in size bands
                      </p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                        {reviewSummaryModel.schoolsInSizeBands.toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        In tuition / Outside tuition
                      </p>
                      <p className="mt-1 text-lg font-bold tabular-nums leading-snug text-slate-900">
                        <span className="text-emerald-800">
                          {reviewSummaryModel.schoolsAssignedToTuition.toLocaleString()}
                        </span>
                        <span className="mx-1 font-normal text-slate-400">/</span>
                        <span className="text-amber-800">
                          {reviewSummaryModel.schoolsOutsideTuition.toLocaleString()}
                        </span>
                      </p>
                      <p className="mt-1 text-[11px] text-slate-500">
                        Assigned to a tuition band vs still outside any tuition band inside a size
                        band.
                      </p>
                    </div>
                  </div>
                )}

                {reviewSizeBandChartData.length > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <h4 className="text-sm font-semibold text-slate-900">
                      Schools per size band
                    </h4>
                    <p className="mb-3 text-xs text-slate-500">
                      Total schools falling in each enrollment (size) band.
                    </p>
                    <div className="h-[260px] w-full min-w-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={reviewSizeBandChartData}
                          margin={{ top: 4, right: 8, left: 4, bottom: 48 }}
                        >
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11 }}
                            interval={0}
                            angle={-28}
                            textAnchor="end"
                            height={56}
                          />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip
                            formatter={(v: number) => [v.toLocaleString(), 'Schools']}
                            labelFormatter={(l) => String(l)}
                          />
                          <Bar
                            dataKey="schools"
                            name="Schools"
                            fill="#ea580c"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={48}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {reviewTuitionNameChartData.length > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <h4 className="text-sm font-semibold text-slate-900">
                      Schools by tuition band name
                    </h4>
                    <p className="mb-3 text-xs text-slate-500">
                      Aggregated across all size bands (top {reviewTuitionNameChartData.length}{' '}
                      labels by count).
                    </p>
                    <div className="h-[280px] w-full min-w-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={reviewTuitionNameChartData}
                          layout="vertical"
                          margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                        >
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                          <YAxis
                            type="category"
                            dataKey="name"
                            width={120}
                            tick={{ fontSize: 10 }}
                            interval={0}
                          />
                          <Tooltip
                            formatter={(v: number) => [v.toLocaleString(), 'Schools']}
                          />
                          <Bar
                            dataKey="schools"
                            fill="#dc2626"
                            radius={[0, 4, 4, 0]}
                            maxBarSize={22}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {reviewHeatmapModel && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-slate-900">
                      Size × tuition heatmap
                    </h4>
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                      <span>
                        Fire gradient: hotter = more schools (max{' '}
                        <span className="font-semibold tabular-nums text-slate-800">
                          {reviewHeatmapModel.maxCount.toLocaleString()}
                        </span>
                        ). Click a cell with schools to list them. Stripes = not applicable.
                      </span>
                      <div className="flex items-center gap-1.5">
                        <span
                          className="h-3 w-10 rounded border border-amber-200/80"
                          style={{ backgroundColor: '#fffbeb' }}
                        />
                        <span aria-hidden>→</span>
                        <span
                          className="h-3 w-10 rounded border border-red-900/30"
                          style={{ backgroundColor: 'hsl(0 85% 42%)' }}
                        />
                      </div>
                    </div>

                    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                      <table className="w-full min-w-[720px] border-collapse text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-50">
                            <th
                              scope="col"
                              className="sticky left-0 z-10 min-w-[12rem] border-r border-slate-200 bg-slate-50 px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                            >
                              Tuition band (under size band)
                            </th>
                            {reviewHeatmapModel.columns.map((col) => (
                              <th
                                key={col.key}
                                scope="col"
                                className="min-w-[6.5rem] px-2 py-3 text-center align-bottom"
                              >
                                <div className="font-semibold text-slate-900">{col.name}</div>
                                <div className="mt-1 text-[11px] font-normal tabular-nums leading-tight text-slate-500">
                                  {col.studentMin.toLocaleString()}–
                                  {col.studentMax.toLocaleString()} students
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {reviewHeatmapModel.groups.map((g, gi) => (
                            <Fragment key={`sb-group-${g.schoolBandIndex}`}>
                              <tr
                                className={`${
                                  gi > 0 ? 'border-t-2 border-indigo-200 ' : ''
                                }bg-gradient-to-r from-indigo-50 via-indigo-50/80 to-slate-50`}
                              >
                                <td
                                  colSpan={
                                    1 + reviewHeatmapModel.columns.length
                                  }
                                  className="px-3 py-3"
                                >
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                    <span className="rounded-md bg-indigo-600 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-white">
                                      School size band
                                    </span>
                                    <span className="text-base font-bold text-indigo-950">
                                      {g.name}
                                    </span>
                                    <span className="text-sm font-medium tabular-nums text-indigo-900">
                                      {g.studentMin.toLocaleString()}–
                                      {g.studentMax.toLocaleString()} students
                                    </span>
                                    <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-indigo-800 shadow-sm ring-1 ring-indigo-200">
                                      {g.rowIndices.length} tuition band
                                      {g.rowIndices.length !== 1 ? 's' : ''}{' '}
                                      below
                                    </span>
                                  </div>
                                  <p className="mt-2 text-xs leading-relaxed text-indigo-900/85">
                                    The following rows are tuition tiers defined only for this
                                    enrollment band. Counts appear under the{' '}
                                    <span className="font-semibold">&quot;{g.name}&quot;</span>{' '}
                                    column; other columns stay not applicable for these rows.
                                  </p>
                                </td>
                              </tr>
                              {g.rowIndices.map((ri) => {
                                const row = reviewHeatmapModel.rows[ri];
                                return (
                                  <tr
                                    key={row.key}
                                    className="border-b border-slate-100 bg-white last:border-0"
                                  >
                                    <th
                                      scope="row"
                                      className="sticky left-0 z-10 max-w-[14rem] border-r border-slate-200 bg-slate-50/80 px-3 py-2.5 pl-6 text-left align-top"
                                    >
                                      <div className="border-l-4 border-l-orange-500 pl-2">
                                        <div className="font-semibold leading-snug text-slate-900">
                                          {row.tuitionName}
                                        </div>
                                        <div className="mt-0.5 tabular-nums text-xs text-slate-500">
                                          {formatTuitionAmount(row.min)} –{' '}
                                          {formatTuitionAmount(row.max)}
                                        </div>
                                      </div>
                                    </th>
                                    {reviewHeatmapModel.columns.map((col, ci) => {
                                      const count =
                                        reviewHeatmapModel.counts[ri][ci] ?? null;
                                      const isOwnColumn =
                                        ci === row.schoolBandIndex;
                                      const canClick =
                                        isOwnColumn &&
                                        count != null &&
                                        count > 0;
                                      return (
                                        <td
                                          key={`${col.key}-${row.key}`}
                                          role={canClick ? 'button' : undefined}
                                          tabIndex={canClick ? 0 : undefined}
                                          className={`px-1 py-1 text-center align-middle transition-opacity ${
                                            isOwnColumn
                                              ? 'ring-1 ring-inset ring-indigo-200/80'
                                              : ''
                                          } ${
                                            canClick
                                              ? 'hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500'
                                              : ''
                                          }`}
                                          style={reviewHeatmapCellStyle(
                                            count,
                                            reviewHeatmapModel.maxCount,
                                            canClick
                                          )}
                                          title={
                                            count === null
                                              ? 'This tuition row applies only to its size band column'
                                              : `${row.schoolBandName} — ${row.tuitionName}: ${count} schools`
                                          }
                                          onClick={() => {
                                            if (!canClick) return;
                                            setOutOfBandNameSearch('');
                                            setOutOfBandNameSearchDebounced(
                                              ''
                                            );
                                            setOutOfBandTablePage(1);
                                            setOutOfBandModal({
                                              type: 'heatmap',
                                              title: `${row.schoolBandName} → ${row.tuitionName} (${formatTuitionAmount(row.min)}–${formatTuitionAmount(row.max)})`,
                                              schoolIds: row.schoolIds,
                                            });
                                          }}
                                          onKeyDown={(e) => {
                                            if (
                                              canClick &&
                                              (e.key === 'Enter' ||
                                                e.key === ' ')
                                            ) {
                                              e.preventDefault();
                                              setOutOfBandNameSearch('');
                                              setOutOfBandNameSearchDebounced(
                                                ''
                                              );
                                              setOutOfBandTablePage(1);
                                              setOutOfBandModal({
                                                type: 'heatmap',
                                                title: `${row.schoolBandName} → ${row.tuitionName} (${formatTuitionAmount(row.min)}–${formatTuitionAmount(row.max)})`,
                                                schoolIds: row.schoolIds,
                                              });
                                            }
                                          }}
                                        >
                                          <span className="inline-block min-w-[2rem] py-2 text-sm font-semibold tabular-nums">
                                            {count === null
                                              ? '—'
                                              : count.toLocaleString()}
                                          </span>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <p className="text-xs text-slate-500">
                      Each shaded block is one school size band and lists its tuition bands. Only
                      the matching column shows counts; the light ring marks that column within the
                      block. Zero means no schools in that tier yet.
                    </p>
                  </div>
                )}
                </section>

                <section
                  className="space-y-4 rounded-xl border border-violet-100 bg-violet-50/15 p-5 ring-1 ring-violet-100/70"
                  aria-labelledby="summary-b-heading"
                >
                  <h3 id="summary-b-heading" className="text-base font-semibold text-slate-900">
                    B. Parent-facing tuition band — distribution
                  </h3>
                  <p className="text-sm text-slate-600">
                    National parent-facing tuition bands (same data as step 4). This view is separate
                    from the school benchmarking heatmap above.
                  </p>
                  <ParentTuitionBandsTable
                    mode="view"
                    rows={summaryParentBandRows}
                    onOpenSchools={openParentBandSchools}
                    onOpenOutsideSchools={openParentMarketOutsideSchools}
                    totalCount={parentBandsQuery.data?.totalCount ?? 0}
                    totalNumberOfSchools={summaryParentListAggregates.totalNumberOfSchools ?? null}
                    totalNumberOfSchoolsOutsideBands={
                      summaryParentListAggregates.totalNumberOfSchoolsOutsideBands ?? null
                    }
                    preferDerivedOutsideFromRows={parentBandsDirty}
                    isPending={parentBandsQuery.isPending}
                    isFetching={parentBandsQuery.isFetching}
                    errorMessage={parentBandsErrorMsg}
                    yearLabel={workflow.year}
                    minSchools={workflow.minSchoolsPerBand ?? 0}
                    showChart
                  />
                </section>
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={handleBack}
              disabled={stepIndex === 0}
            >
              Back
            </Button>
            {stepIndex === SUMMARY_STEP_INDEX ? (
              <div className="flex flex-wrap items-center gap-3">
                {submitPending && (
                  <span className="text-sm text-slate-500">Submitting…</span>
                )}
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => void handleSubmitClassification()}
                  disabled={!canSubmitClassification}
                >
                  Submit classification
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="primary"
                onClick={handleNext}
                disabled={!canGoNext}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      )}

      <Modal
        title={
          outOfBandModal?.type === 'tuition' || outOfBandModal?.type === 'heatmap'
            ? outOfBandModal.title
            : 'Schools outside size bands'
        }
        open={outOfBandModal != null}
        onCancel={() => setOutOfBandModal(null)}
        footer={null}
        width={880}
        destroyOnClose
      >
        {outOfBandError && (
          <Alert type="error" message={outOfBandError} className="mb-4" />
        )}
        {!outOfBandError &&
          outOfBandModal?.type === 'size' &&
          totalSchoolOutOfBandsIds.length === 0 &&
          totalSchoolOutOfBands > 0 && (
            <Alert
              type="info"
              showIcon
              className="mb-4"
              message="School list is not ready yet"
              description="Wait until school counts finish updating after you change bands. Then open this list again to load schools by ID."
            />
          )}
        {!outOfBandError && modalSchoolIds.length > 0 && (
          <div>
            <Input
              allowClear
              placeholder="Search by school name or code…"
              value={outOfBandNameSearch}
              onChange={(e) => setOutOfBandNameSearch(e.target.value)}
              className="mb-3"
              aria-label="Search schools by name or educational center code"
            />
            <Spin spinning={outOfBandLoading}>
              <div className="max-h-[min(60vh,28rem)] overflow-auto rounded-lg border border-slate-200">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <th className="px-3 py-2">School</th>
                      <th className="px-3 py-2 whitespace-nowrap">Code</th>
                      <th className="px-3 py-2 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={toggleOutOfBandStudentsSort}
                          className="inline-flex items-center gap-1 rounded px-1 py-0.5 font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
                          aria-sort={
                            outOfBandStudentsSort === 'asc' ? 'ascending' : 'descending'
                          }
                        >
                          Students
                          <span
                            className="text-indigo-600 tabular-nums normal-case"
                            aria-hidden
                          >
                            {outOfBandStudentsSort === 'asc' ? '↑' : '↓'}
                          </span>
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {outOfBandItems.length === 0 && !outOfBandLoading ? (
                      <tr>
                        <td colSpan={3} className="px-3 py-6 text-center text-slate-500">
                          No schools listed.
                        </td>
                      </tr>
                    ) : (
                      outOfBandItems.map((row, idx) => {
                        const { label, code, students } = formatSchoolByIdsRow(row);
                        return (
                          <tr
                            key={String(
                              row.id ?? `${label}-${outOfBandTablePage}-${idx}`
                            )}
                            className="border-b border-slate-100 last:border-0"
                          >
                            <td className="px-3 py-2 text-slate-900">{label}</td>
                            <td className="px-3 py-2 font-mono text-xs tabular-nums text-slate-800">
                              {code}
                            </td>
                            <td className="px-3 py-2 tabular-nums text-slate-800">
                              {students.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {outOfBandTotalCount > 0 && (
                <div className="mt-3 flex justify-end">
                  <Pagination
                    size="small"
                    current={outOfBandTablePage}
                    pageSize={OUT_OF_BAND_PAGE_SIZE}
                    total={outOfBandTotalCount}
                    onChange={setOutOfBandTablePage}
                    showSizeChanger={false}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} schools`
                    }
                  />
                </div>
              )}
            </Spin>
          </div>
        )}
        {!outOfBandError &&
          outOfBandModal?.type === 'heatmap' &&
          modalSchoolIds.length === 0 &&
          !outOfBandLoading && (
            <Alert
              type="info"
              showIcon
              className="mb-4"
              message="No individual school IDs for this cell"
              description="The API did not return school IDs for this tuition band. Run Recalculate school assignments on the tuition step, or confirm the backend populates nested schoolIds."
            />
          )}
        {!outOfBandError &&
          outOfBandModal &&
          modalSchoolIds.length === 0 &&
          !outOfBandLoading &&
          (outOfBandModal.type === 'tuition' ||
            (outOfBandModal.type === 'size' &&
              totalSchoolOutOfBandsIds.length === 0 &&
              totalSchoolOutOfBands === 0)) && (
            <p className="py-6 text-center text-sm text-slate-500">
              {outOfBandModal.type === 'tuition'
                ? 'No school IDs are listed for this case. Run recalculate on the tuition step to refresh assignments.'
                : 'No schools to list.'}
            </p>
          )}
      </Modal>

      <SizeBandSchoolsEmbeddedModal
        open={sizeBandSchoolsModal != null}
        title={sizeBandSchoolsModal?.title ?? ''}
        schools={sizeBandSchoolsModal?.schools ?? []}
        onClose={() => setSizeBandSchoolsModal(null)}
      />

      <TuitionBandSchoolsEmbeddedModal
        open={tuitionBandSchoolsModal != null}
        title={tuitionBandSchoolsModal?.title ?? ''}
        schools={tuitionBandSchoolsModal?.schools ?? []}
        variant={tuitionBandSchoolsModal?.variant}
        onClose={() => setTuitionBandSchoolsModal(null)}
      />

      <Modal
        title={parentSchoolModal?.title ?? 'Schools in band'}
        open={parentSchoolModal != null}
        onCancel={() => setParentSchoolModal(null)}
        footer={null}
        width={880}
        destroyOnClose
      >
        {parentSchoolListQuery.isError &&
          !parentOutsideUsesEmbeddedList &&
          !parentModalEmbeddedBand && (
          <Alert
            type="error"
            message={
              parentSchoolListQuery.error instanceof Error
                ? parentSchoolListQuery.error.message
                : 'Could not load schools.'
            }
            className="mb-4"
          />
        )}
        {parentSchoolModal?.variant === 'parentMarketOutside' && (
          <Input
            allowClear
            placeholder="Search by school name or code…"
            value={parentMarketOutsideSearch}
            onChange={(e) => setParentMarketOutsideSearch(e.target.value)}
            className="mb-3"
            aria-label="Search schools by name or educational center code"
          />
        )}
        <Spin spinning={parentModalQueryLoading}>
          <div className="max-h-[min(60vh,28rem)] overflow-auto rounded-lg border border-slate-200">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <th className="px-3 py-2">School name</th>
                  <th className="px-3 py-2 whitespace-nowrap">Educational center #</th>
                  <th className="px-3 py-2 whitespace-nowrap">
                    {parentSchoolModal?.variant === 'parentMarketOutside' ? (
                      <button
                        type="button"
                        onClick={toggleParentMarketOutsideStudentsSort}
                        className="inline-flex items-center gap-1 rounded px-1 py-0.5 font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
                        aria-sort={
                          parentMarketOutsideStudentsSort === 'asc' ? 'ascending' : 'descending'
                        }
                      >
                        Students
                        <span
                          className="text-indigo-600 tabular-nums normal-case"
                          aria-hidden
                        >
                          {parentMarketOutsideStudentsSort === 'asc' ? '↑' : '↓'}
                        </span>
                      </button>
                    ) : (
                      'Students'
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {parentModalDisplayItems.length === 0 && !parentModalQueryLoading ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-6 text-center text-slate-500">
                      No schools returned.
                    </td>
                  </tr>
                ) : (
                  parentModalDisplayItems.map((row, idx) => {
                    const { label, code, students } = formatSchoolByIdsRow(row);
                    return (
                      <tr
                        key={String(row.id || idx)}
                        className="border-b border-slate-100 last:border-0"
                      >
                        <td className="px-3 py-2 text-slate-900">{label}</td>
                        <td className="px-3 py-2 font-mono text-xs tabular-nums text-slate-800">
                          {code}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-slate-800">
                          {students.toLocaleString()}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Spin>
        {parentModalDisplayTotal > 0 && (
          <div className="mt-3 flex justify-end">
            <Pagination
              size="small"
              current={parentSchoolModalPage}
              pageSize={PARENT_SCHOOL_MODAL_PAGE_SIZE}
              total={parentModalDisplayTotal}
              onChange={setParentSchoolModalPage}
              showSizeChanger={false}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} schools`
              }
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
